import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Community, HydratedPost, PostType, Post, UserData, ChannelMessage, ChannelType, Conversation, VoiceParticipant } from '../types';
import { useDynamicManifest } from '../hooks/useDynamicManifest';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import api, {
    fetchCommunityDetails,
    fetchCommunityPosts,
    joinCommunityOnServer,
    leaveCommunityOnServer,
    updateCommunityOnServer,
    grantAdminOnServer,
    revokeAdminOnServer,
    removeMemberOnServer,
    fetchChannelMessages,
    sendChannelMessage,
    updateUserOnServer,
    reorderChannels,
    reactToChannelMessage,
    deleteChannelMessage,
    editChannelMessage,
    approveCommunityPostOnServer,
    API_URL
} from '../api';
import { getWeightedUserList } from '../utils/interactionUtils';
import { FormattedText } from './FormattedText';
import { PostCreator } from './PostCreator';
import { handleSliderPointerDown, readFromClipboard, copyToClipboard } from '../utils/helpers';
import { stickerTextStyle } from './StickerText';

import { PostGrid } from './PostGrid';
import { TiktokEmbed } from './PostCard';
import { PfpCropperModal } from './PfpCropperModal';
import { compressImage, uploadMediaFile, compressVideo, compressAudio } from '../utils/mediaUtils';
import { CameraIcon, VideoIcon, VolumeHighIcon, PhoneIcon, HashtagIcon, GalleryIcon, XIcon, SendIcon, MicIcon, PaperclipIcon, TrashIcon, ReplyIcon, CopyIcon, MoreHorizontalIcon, CloseIcon, EmojiIcon, EditIcon, PlusIcon, ChevronLeftIcon, PasteIcon, ImageIcon, UsersIcon, SearchIcon, SparklesIcon } from './Icons';
import { AudioRecorder, CustomAudioPlayer, AudioRecorderHandle } from './AudioComponents';
import { SettingsIcon, ExternalLinkIcon, ChevronRightIcon } from './Icons';
import { MediaViewerModal } from './MediaViewerModal';
import { useUpload } from '../contexts/UploadContext';
import { useAuth } from '../contexts/AuthContext';
import { requestMicrophonePermission, VOICE_AUDIO_CONSTRAINTS } from '../utils/permissionUtils';
import { Attachment, MediaAttachment as MediaAttachmentType, MediaType, Channel } from '../types';
import { useScrollLock } from '../hooks/useScrollLock';
import { EmojiPickerModal } from './EmojiPickerModal';
import { GifPickerModal, GifItem } from './GifPickerModal';
import { LinkifyText } from './LinkifyText';
import { DeleteMessageModal } from './MessageModals';
import { RemoteStreamView } from './VoiceRoom';
import { NativeCamera } from './NativeCamera';
import { QRCodeSVG } from 'qrcode.react';
import { PostScreenshot } from './PostScreenshot';
import { SlideshowBuilder } from './SlideshowBuilder';
import { toPng } from 'html-to-image';
import html2canvas from 'html2canvas';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { getSlideIntervalMs, reorderSlideshowAttachments } from '../utils/slideshowUtils';

const DragHandleIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="9" cy="5" r="1" />
        <circle cx="9" cy="12" r="1" />
        <circle cx="9" cy="19" r="1" />
        <circle cx="15" cy="5" r="1" />
        <circle cx="15" cy="12" r="1" />
        <circle cx="15" cy="19" r="1" />
    </svg>
);

const hexToRgba = (hex: string, opacity: number) => {
    try {
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
            cleanHex = cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2];
        }
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return isNaN(r) || isNaN(g) || isNaN(b) ? null : `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch (e) {
        return null;
    }
};

const getEmojiSizeClass = (text: string) => {
    if (!text) return '';
    const trimmed = text.trim();
    if (trimmed.length === 0) return '';

    const emojiRegex = /\p{Extended_Pictographic}\uFE0F?[\u{1F3FB}-\u{1F3FF}]?(?:\u200D\p{Extended_Pictographic}\uFE0F?[\u{1F3FB}-\u{1F3FF}]?)*/gu;
    const stripped = trimmed.replace(emojiRegex, '').replace(/\s/g, '');

    if (stripped.length > 0) return '';

    // If it's pure emojis, count them to determine size
    const matches = trimmed.match(emojiRegex);
    const count = matches ? matches.length : 0;

    if (count === 1) return 'text-7xl py-4 flex justify-center w-full';
    if (count === 2) return 'text-5xl py-3';
    if (count === 3) return 'text-4xl py-2';
    return 'text-2xl py-1';
};

const ChannelMessageComponent = React.memo(({
    msg,
    isGrouped,
    isMine,
    isAdminOrCreator,
    currentUserId,
    usersData,
    messageFontSize,
    selectedMessageId,
    editingMessageId,
    editText,
    setEditText,
    setEditingMessageId,
    handleSaveEdit,
    handleReply,
    handleReact,
    handleMessageDragStart,
    handleMessageDragMove,
    handleMessageDragEnd,
    dragOffsets,
    MAX_DRAG_DISTANCE,
    scrollToMessage,
    copyToClipboard,
    setDeletingMessage,
    setActiveMenu,
    activeMenu,
    setShowEmojiPickerFor,
    onNavigateToProfile,
    recentEmojis,
    updateRecentEmojis,
    onDoubleTap,
    onMediaClick
}: {
    msg: ChannelMessage;
    isGrouped: boolean;
    isMine: boolean;
    isAdminOrCreator: boolean;
    currentUserId: string;
    usersData: Record<string, UserData>;
    messageFontSize: number;
    selectedMessageId: string | null;
    editingMessageId: string | null;
    editText: string;
    setEditText: (t: string) => void;
    setEditingMessageId: (id: string | null) => void;
    handleSaveEdit: (id: string) => void;
    handleReply: (m: ChannelMessage) => void;
    handleReact: (id: string, emoji: string) => void;
    handleMessageDragStart: (e: any, id: string) => void;
    handleMessageDragMove: (e: any, id: string) => void;
    handleMessageDragEnd: (id: string) => void;
    dragOffsets: Record<string, number>;
    MAX_DRAG_DISTANCE: number;
    scrollToMessage: (id: string) => void;
    copyToClipboard: (text: string) => void;
    setDeletingMessage: (m: ChannelMessage | null) => void;
    setActiveMenu: (id: string | null) => void;
    activeMenu: string | null;
    setShowEmojiPickerFor: (id: string | null) => void;
    onNavigateToProfile: (userId: string) => void;
    recentEmojis: string[];
    updateRecentEmojis: (emoji: string) => void;
    onDoubleTap?: (id: string) => void;
    onMediaClick?: (msgId: string, mediaIndex: number) => void;
}) => {
    const lastTouchTimeRef = useRef<number>(0);
    const handleTouchStart = (e: React.TouchEvent) => {
        const now = Date.now();
        if (now - lastTouchTimeRef.current < 300) {
            onDoubleTap?.(msg.id);
            lastTouchTimeRef.current = 0;
            e.preventDefault();
        } else {
            lastTouchTimeRef.current = now;
        }
        handleMessageDragStart(e, msg.id);
    };
    const offset = dragOffsets[msg.id] || 0;
    const emojiSizeClass = getEmojiSizeClass(msg.text);
    const isStandalone = !!emojiSizeClass;

    const youtubeVideoId = useMemo(() => {
        if (!msg.text) return null;
        const match = msg.text.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
        return match ? match[1] : null;
    }, [msg.text]);

    const isYoutubeOnly = useMemo(() => {
        if (!youtubeVideoId || !msg.text) return false;
        const textWithoutUrl = msg.text.replace(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)[a-zA-Z0-9_-]{11}[^\s]*/gi, '').trim();
        return textWithoutUrl === '' && (!msg.media || msg.media.length === 0);
    }, [youtubeVideoId, msg.text, msg.media]);

    const tiktokVideoId = useMemo(() => {
        if (!msg.text) return null;
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const textUrls = msg.text.match(urlRegex) || [];
        const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/.*\/video\/(\d+)/;
        
        for (const url of textUrls) {
            const match = url.match(tiktokRegex);
            if (match && match[1]) {
                return match[1];
            }
        }
        return null;
    }, [msg.text]);

    const [resolvedTiktokVideoId, setResolvedTiktokVideoId] = useState<string | null>(null);

    useEffect(() => {
        if (tiktokVideoId) {
            setResolvedTiktokVideoId(tiktokVideoId);
            return;
        }

        if (!msg.text) {
            setResolvedTiktokVideoId(null);
            return;
        }

        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const textUrls = msg.text.match(urlRegex) || [];
        const tiktokUrl = textUrls.find(url => url.includes('tiktok.com'));

        if (tiktokUrl) {
            const resolveTiktok = async () => {
                try {
                    const response = await api.post('metadata', { url: tiktokUrl });
                    if (response.data && response.data.url) {
                        const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/.*\/video\/(\d+)/;
                        const resolvedMatch = response.data.url.match(tiktokRegex);
                        if (resolvedMatch && resolvedMatch[1]) {
                            setResolvedTiktokVideoId(resolvedMatch[1]);
                        }
                    }
                } catch (e) {
                    console.error("Failed to resolve TikTok URL inside channel message:", e);
                }
            };
            resolveTiktok();
        } else {
            setResolvedTiktokVideoId(null);
        }
    }, [tiktokVideoId, msg.text]);

    const isTiktokOnly = useMemo(() => {
        if (!resolvedTiktokVideoId || !msg.text) return false;
        const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/[^\s]*/gi;
        const textWithoutUrl = msg.text.replace(tiktokRegex, '').trim();
        return textWithoutUrl === '' && (!msg.media || msg.media.length === 0);
    }, [resolvedTiktokVideoId, msg.text, msg.media]);

    return (
        <div id={`message-${msg.id}`} className="group relative message-item"
            onMouseDown={(e) => handleMessageDragStart(e, msg.id)}
            onMouseMove={(e) => handleMessageDragMove(e, msg.id)}
            onMouseUp={() => handleMessageDragEnd(msg.id)}
            onMouseLeave={() => handleMessageDragEnd(msg.id)}
            onTouchStart={handleTouchStart}
            onTouchMove={(e) => handleMessageDragMove(e, msg.id)}
            onTouchEnd={() => handleMessageDragEnd(msg.id)}
            onDoubleClick={() => onDoubleTap?.(msg.id)}
        >
            {/* Drag Indicator */}
            {offset > 0 && (
                <div
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full pr-2 transition-opacity"
                    style={{ opacity: offset / MAX_DRAG_DISTANCE }}
                >
                    <ReplyIcon className="w-5 h-5 text-blue-500" />
                </div>
            )}

            <div
                className={`px-1 py-0.5 transition-all duration-200 cursor-pointer ${selectedMessageId === msg.id ? 'ring-1 ring-white/50 bg-white/5 rounded-[22px]' : 'hover:bg-white/5 rounded-[22px]'}`}
            >
                <div className={`flex gap-2 px-1 ${isGrouped ? 'mt-0' : 'mt-1'}`}>

                    {/* Avatar column */}
                    <div className="flex-shrink-0 w-8">
                        {!isGrouped ? (
                            usersData[msg.authorId]?.pfp ? (
                                <img src={usersData[msg.authorId].pfp} alt={msg.authorId} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                                    style={{ backgroundColor: usersData[msg.authorId]?.appThemeColor || '#2563eb' }}
                                >
                                    {msg.authorId[0]?.toUpperCase()}
                                </div>
                            )
                        ) : (
                            <span className="text-[10px] text-gray-600 group-hover:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity leading-tight flex items-center justify-end w-full pr-1 select-none">
                                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        {!isGrouped && (
                            <div className="flex items-center gap-2 mb-0.5">
                                <span
                                    className="text-sm text-raised font-bold text-white hover:underline cursor-pointer"
                                    style={{ color: usersData[msg.authorId]?.appThemeColor || '#2563eb', ...stickerTextStyle(0.18) }}
                                    onClick={() => onNavigateToProfile(msg.authorId)}
                                >
                                    {usersData[msg.authorId]?.displayName || msg.authorId}
                                </span>
                                <span className="text-[10px] text-gray-500 font-medium">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                        )}

                        {/* Reply Style: Thin connecting line and author label above */}
                        {msg.replyTo && (
                            <div
                                className="mb-1 flex items-center gap-2 opacity-60 hover:opacity-100 cursor-pointer transition-opacity max-w-[80%] group/reply"
                                onClick={(e) => { e.stopPropagation(); scrollToMessage(msg.replyTo!.id); }}
                            >
                                <div className="w-8 h-4 border-l-2 border-t-2 border-white/20 rounded-tl-lg ml-4 mb-[-8px]" />
                                <div className="flex items-center gap-1.5 min-w-0">
                                    {usersData[msg.replyTo.authorId]?.pfp ? (
                                        <img src={usersData[msg.replyTo.authorId].pfp} className="w-4 h-4 rounded-full" alt="" />
                                    ) : (
                                        <div className="w-4 h-4 rounded-full bg-gray-700 flex items-center justify-center text-[8px] font-bold">
                                            {msg.replyTo.authorId[0]?.toUpperCase()}
                                        </div>
                                    )}
                                    <span className="text-[11px] font-bold text-gray-400 group-hover/reply:text-blue-400" style={stickerTextStyle(0.12)}>
                                        {msg.replyTo.authorId === currentUserId ? (usersData[currentUserId]?.displayName || 'You') : (usersData[msg.replyTo.authorId]?.displayName || msg.replyTo.authorId)}
                                    </span>
                                    <p className="text-[11px] text-gray-500 truncate italic">
                                        {msg.replyTo.text}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Message Content Container */}
                        <div className="relative inline-block max-w-full">
                            {editingMessageId === msg.id ? (
                                <div className="flex flex-col gap-2 mt-1 min-w-[300px] w-full max-w-md">
                                    <textarea
                                        autoFocus
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSaveEdit(msg.id);
                                            } else if (e.key === 'Escape') {
                                                setEditingMessageId(null);
                                            }
                                        }}
                                        className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[60px] resize-none"
                                    />
                                    <div className="flex gap-2 text-[10px]">
                                        <span className="text-gray-500">escape to <button onClick={() => setEditingMessageId(null)} className="text-blue-400 hover:underline">cancel</button></span>
                                        <span className="text-gray-500">•</span>
                                        <span className="text-gray-500">enter to <button onClick={() => handleSaveEdit(msg.id)} className="text-blue-400 hover:underline">save</button></span>
                                    </div>
                                </div>
                            ) : (
                                (msg.text && !isYoutubeOnly && !isTiktokOnly) && (
                                    <div className="flex flex-col">
                                        <div className={`text-gray-300 text-raised break-words leading-snug whitespace-pre-wrap ${isStandalone ? emojiSizeClass : ''}`}>
                                            <FormattedText
                                                text={msg.text}
                                                onNavigateToProfile={onNavigateToProfile}
                                                onNavigateToHashtag={() => { }}
                                                fontSize={isStandalone ? undefined : messageFontSize}
                                                stickerize={true}
                                            />
                                        </div>
                                        {msg.lastEdited && (
                                            <span className="text-[10px] text-gray-600 italic mt-0.5">(edited)</span>
                                        )}
                                    </div>
                                )
                            )}
                            {msg.media && msg.media.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {msg.media.map((m, i) => (
                                        <div key={i} className="max-w-[16rem] sm:max-w-md rounded-xl overflow-hidden border border-white/10 bg-black/40 shadow-xl group/media relative">
                                            {m.type === 'image' && <img src={m.url} className="max-w-full h-auto cursor-pointer hover:scale-[1.02] transition-transform duration-300" alt="attachment" onClick={() => onMediaClick?.(msg.id, i)} />}
                                            {m.type === 'video' && (
                                                <div className="relative group/msg-video">
                                                    <video key={m.url} src={`${m.url}#t=0.001`} controls playsInline preload="metadata" className="max-w-full h-auto" />
                                                    <button
                                                        onClick={() => onMediaClick?.(msg.id, i)}
                                                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/msg-video:opacity-100 transition-all border border-white/10 z-10"
                                                        title="Enlarge Video"
                                                    >
                                                        <ExternalLinkIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                            {(m.type === 'audio' || m.type === 'voice') && (
                                                <div className="relative group/msg-audio w-full">
                                                    <div className="flex flex-col gap-1 w-full min-w-[200px] p-2">
                                                        <CustomAudioPlayer src={m.url} autoplay={false} />
                                                        <span className="text-[10px] text-gray-500 px-1 capitalize">{m.type}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => onMediaClick?.(msg.id, i)}
                                                        className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/40 hover:bg-black/60 text-white/70 hover:text-white opacity-0 group-hover/msg-audio:opacity-100 transition-all border border-white/10 z-10"
                                                        title="Enlarge Audio"
                                                    >
                                                        <ExternalLinkIcon className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {resolvedTiktokVideoId ? (
                                <div className="mt-2 w-[280px] h-[497px] max-w-full rounded-2xl overflow-hidden shadow-lg border border-white/10 relative flex items-center justify-center bg-black">
                                    <TiktokEmbed videoId={resolvedTiktokVideoId} apiUrl={API_URL} />
                                </div>
                            ) : youtubeVideoId ? (
                                <div className="mt-2 w-[280px] max-w-full rounded-2xl overflow-hidden shadow-lg border border-white/10 aspect-video">
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        src={`${API_URL}youtube-proxy?videoId=${youtubeVideoId}&origin=${encodeURIComponent(window.location.origin)}`}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        className="w-full h-full"
                                    />
                                </div>
                            ) : null}

                            {/* Reactions display */}
                            {msg.reactions && Object.entries(msg.reactions).some(([_, users]) => users.length > 0) && (
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                    {Object.entries(msg.reactions).map(([emoji, users]) => users.length > 0 && (
                                        <button
                                            type="button"
                                            key={emoji}
                                            onClick={(e) => { e.stopPropagation(); handleReact(msg.id, emoji); }}
                                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-base border transition-all ${users.includes(currentUserId)
                                                ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                                                }`}
                                            title={users.join(', ')}
                                        >
                                            <span className="text-xl sticker-outline">{emoji}</span>
                                            {users.length > 1 && <span className="text-xs font-bold ml-0.5">{users.length}</span>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* Options Menu Trigger - Now only shows "..." on hover */}
            <div className="absolute top-4   right-4 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1  rounded-lg p-1 shadow-2xl z-20 -translate-y-1/2">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === msg.id ? null : msg.id);
                    }}
                    className="p-1 text-gray-400 hover:text-white transition-colors"
                >
                    <MoreHorizontalIcon className="w-5 h-5" />
                </button>
            </div>

            {/* Full Menu - Includes Quick Reactions and other options - Repositioned to center screen */}
            {activeMenu === msg.id && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveMenu(null)}>
                    <div className="w-full max-w-xs bg-[#1b1b1f] border border-white/10 rounded-3xl shadow-2xl overflow-hidden p-2 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                        <div className="p-1.5">
                            <button onClick={() => { handleReply(msg); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                <ReplyIcon className="w-4 h-4 text-blue-400" />
                                <span>Reply</span>
                            </button>
                            {isMine && (
                                <button onClick={() => { setEditingMessageId(msg.id); setEditText(msg.text); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                    <EditIcon className="w-4 h-4 text-orange-400" />
                                    <span>Edit Message</span>
                                </button>
                            )}
                            <button onClick={() => { copyToClipboard(msg.text); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                <CopyIcon className="w-4 h-4 text-green-400" />
                                <span>Copy Text</span>
                            </button>
                            {(isMine || isAdminOrCreator) && (
                                <button onClick={() => { setDeletingMessage(msg); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-red-500/10 text-red-400 rounded-2xl text-sm transition-colors">
                                    <TrashIcon className="w-4 h-4" />
                                    <span>Delete</span>
                                </button>
                            )}
                        </div>
                        <div className="p-3 border-t border-white/5 mt-2 flex justify-between gap-1 overflow-x-auto">
                            {recentEmojis.slice(0, 6).map(emoji => (
                                <button
                                    key={emoji}
                                    onClick={() => {
                                        handleReact(msg.id, emoji);
                                        updateRecentEmojis(emoji);
                                        setActiveMenu(null);
                                    }}
                                    className="text-xl hover:scale-125 transition-transform flex items-center justify-center"
                                >
                                    <span className="sticker-outline">{emoji}</span>
                                </button>
                            ))}
                            <button onClick={() => { setShowEmojiPickerFor(msg.id); setActiveMenu(null); }} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-gray-400"><PlusIcon className="w-4 h-4" /></button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

interface CommunityDetailPageProps {
    query: string;
    currentUserId: string;
    onPostClick: (post: HydratedPost) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number, post?: HydratedPost) => void;
    onNavigateToProfile: (userId: string) => void;
    onHoldProfile?: (userId: string) => void;
    onNavigateToHashtag: (tag: string) => void;
    onNavigateToCommunity: (id: string, channelId?: string) => void;
    onCreatePost: (data: any) => any;
    onVouch: (postId: number) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    galleryAnimation: string;
    postBlurOpacity: number;
    usersData: Record<string, UserData>;
    gridColumns?: 1 | 2 | 3 | 4 | 5 | 6;
    onGridColumnsChange?: (cols: 1 | 2 | 3 | 4 | 5 | 6) => void;
    preferredScale?: number;
    onScaleChange?: (scale: number) => void;
    onSwipeTransition?: (direction: 'left' | 'right') => void;
    socket: any;
    voiceRooms: Record<string, VoiceParticipant[]>;
    isDemo?: boolean;
    isShakingMode?: boolean;
    postcardGap?: number;
    onPostcardGapChange?: (gap: number) => void;
    onJoinVoice?: (communityId: string, channelId: string) => void;
    onLeaveVoice?: () => void;
    activeVoiceData?: { communityId: string, channelId: string } | null;
    channelId?: string;
    isModalOpen?: boolean;
    messageFontSize: number;
    updateMessageFontSize: (size: number) => void;
    onReportPost: (postId: number) => void;
    onUpdatePostcardLayout: (postId: number, layout: 'square' | 'dynamic') => void;
    messageId?: string;
    onBack?: () => void;
    onShare?: (post: HydratedPost) => void;
    initialVisibleCount?: number;
    onVisibleCountChange?: (count: number) => void;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    isLoading?: boolean;
    initialSharedContent?: { text: string, media: MediaAttachmentType[], sourcePostId?: number } | null;
    clearSharedContent?: () => void;
    getHydratedPosts: (posts: Post[]) => HydratedPost[];
    posts?: Post[];
    conversations?: Conversation[];
    communities?: Community[];
    updateUserData: any;
    voiceRemoteStreams?: Record<string, MediaStream>;
    voiceLocalStream?: MediaStream | null;
    onChannelChange?: (channelId: string) => void;
    onUpdateCommunity?: (community: any) => void;
    onPreviewBgResolution?: (res: number | null) => void;
    onUploadFailed?: (localId: number) => void;
    failedPosts?: HydratedPost[];
    onRetryPostUpload?: (localId: number, startUpload: any) => void;
}
export const CommunityDetailPage = ({
    query,
    currentUserId,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onHoldProfile,
    onNavigateToHashtag,
    onNavigateToCommunity,
    onCreatePost,
    onVouch,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    usersData,
    gridColumns = 1,
    onGridColumnsChange,
    preferredScale = 1,
    onScaleChange,
    onSwipeTransition,
    socket,
    voiceRooms,
    isDemo = false,
    isShakingMode = false,
    postcardGap = 8,
    onPostcardGapChange,
    onJoinVoice,
    onLeaveVoice,
    activeVoiceData,
    updateUserData,
    channelId,
    isModalOpen = false,
    messageFontSize,
    updateMessageFontSize,
    onReportPost,
    onUpdatePostcardLayout,
    messageId,
    onBack,
    onShare,
    initialVisibleCount,
    onVisibleCountChange,
    onInternalModalStateChange,
    closeInternalModalsTrigger = 0,
    isLoading: isGlobalLoading,
    initialSharedContent,
    clearSharedContent,
    getHydratedPosts,
    posts: allPosts = [],
    conversations = [],
    communities = [],
    voiceRemoteStreams = {},
    voiceLocalStream = null,
    onChannelChange,
    onUpdateCommunity,
    onPreviewBgResolution,
    onUploadFailed,
    failedPosts = [],
    onRetryPostUpload
}: CommunityDetailPageProps) => {
    const { currentUser: authUser, usersData: authUsersData } = useAuth();
    const activeUser = authUser || currentUserId;
    const activeUsersData = authUsersData || usersData;
    const currentUserData = activeUsersData[activeUser];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const [community, setCommunity] = useState<Community | null>(() => communities.find(c => c._id === query || c.name === query) || null);

    useDynamicManifest({
        title: community?.name || 'Community',
        iconUrl: community?.avatar || '/assets/goodieboy.png',
        startUrl: `/?community=${community?._id}`,
        themeColor: community?.themeColor,
        isActive: !!community && !isModalOpen
    });

    const [communityPosts, setCommunityPosts] = useState<HydratedPost[]>([]);
    const [isLocalLoading, setIsLocalLoading] = useState<boolean>(() => !communities.some(c => c._id === query || c.name === query));
    const [loadError, setLoadError] = useState<string | null>(null);
    const [showSettings, setShowSettings] = useState(false);

    useEffect(() => {
        if (!showSettings && onPreviewBgResolution) {
            onPreviewBgResolution(null);
        }
    }, [showSettings, onPreviewBgResolution]);
    const [showMembers, setShowMembers] = useState(false);
    const [settingsForm, setSettingsForm] = useState({
        name: '',
        bio: '',
        isPrivate: false,
        requireApprovalForPosts: false,
        whoCanPost: 'all' as 'all' | 'admins',
        whoCanViewMembers: 'all' as 'all' | 'admins',
        avatar: '',
        themeColor: '#161b22',
        bg: { url: '', style: 'color' },
        bgResolution: 100,
        channels: [] as Channel[],
        bgMode: 'image' as 'image' | 'color',
        bgSlideshow: { enabled: true, attachments: [] as Attachment[], intervalValue: 10, intervalUnit: 'seconds' as 'seconds' | 'minutes' | 'hours' | 'days' }
    });
    const activeSlideshow = settingsForm.bgSlideshow || { enabled: true, attachments: [] as any[], intervalValue: 10, intervalUnit: 'seconds' };
    const [showTenorModal, setShowTenorModal] = useState(false);
    const [tenorSearchMode, setTenorSearchMode] = useState<'chat' | 'background'>('chat');
    const [showInputMenu, setShowInputMenu] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraMode, setCameraMode] = useState<'image' | 'video' | 'both'>('both');
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [memberActionLoading, setMemberActionLoading] = useState<string | null>(null);
    const [inviteFeedback, setInviteFeedback] = useState<string | null>(null);
    const [currentChannelId, setCurrentChannelId] = useState<string>(channelId || 'posts');

    useEffect(() => {
        if (channelId) {
            setCurrentChannelId(channelId);
        }
    }, [channelId]);
    const [channelMessages, setChannelMessages] = useState<ChannelMessage[]>([]);
    const [activeMediaViewerIndex, setActiveMediaViewerIndex] = useState<number | null>(null);

    const channelMedia = useMemo(() => {
        const mediaList: any[] = [];
        channelMessages.forEach((msg) => {
            if (msg.media && msg.media.length > 0) {
                msg.media.forEach((attachment, idx) => {
                    mediaList.push({
                        url: attachment.url,
                        type: attachment.type,
                        messageId: msg.id,
                        mediaIndex: idx,
                        senderId: msg.authorId,
                        timestamp: msg.timestamp,
                        caption: msg.text
                    });
                });
            }
        });
        return mediaList;
    }, [channelMessages]);

    const handleMediaClick = useCallback((msgId: string, mediaIndex: number) => {
        const index = channelMedia.findIndex(
            m => m.messageId === msgId && m.mediaIndex === mediaIndex
        );
        if (index !== -1) {
            setActiveMediaViewerIndex(index);
        }
    }, [channelMedia]);
    const [messageInput, setMessageInput] = useState('');
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
    const [sidebarOpen, setSidebarOpen] = useState(typeof window !== 'undefined' && window.innerWidth <= 768 ? false : true);
    const [isNavigationModalOpen, setIsNavigationModalOpen] = useState(false);
    const [voiceChannelToJoin, setVoiceChannelToJoin] = useState<string | null>(null);
    const initialPinchDistanceRef = useRef<number | null>(null);
    const initialFontSizeRef = useRef<number>(14);

    const [hasScrolledToMessage, setHasScrolledToMessage] = useState<string | null>(null);

    const [mentionSearchTerm, setMentionSearchTerm] = useState<string>('');
    const [showMentionDropdown, setShowMentionDropdown] = useState<boolean>(false);
    const [mentionCursorIndex, setMentionCursorIndex] = useState<number>(-1);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState<number>(0);

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteSearchTerm, setInviteSearchTerm] = useState('');
    const [invitingUserIds, setInvitingUserIds] = useState<Set<string>>(new Set());
    const [showShareCommunityModal, setShowShareCommunityModal] = useState(false);
    const [shareCommunityGenerating, setShareCommunityGenerating] = useState(false);
    const [shareCommunityBlob, setShareCommunityBlob] = useState<Blob | null>(null);
    const [showCommunityQRCode, setShowCommunityQRCode] = useState(false);
    const communityShareRef = useRef<HTMLDivElement>(null);

    const [isRecording, setIsRecording] = useState(false);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    const audioRecorderRef = useRef<AudioRecorderHandle>(null);
    const [currentAttachments, setCurrentAttachments] = useState<Attachment[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const { startGenericUpload, startUpload } = useUpload();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pfpFileInputRef = useRef<HTMLInputElement>(null);
    const bgFileInputRef = useRef<HTMLInputElement>(null);
    const [cropImage, setCropImage] = useState<string | null>(null);
    const [isCropping, setIsCropping] = useState(false);
    const [localBgColor, setLocalBgColor] = useState<string>('#1e293b');
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [recentEmojis, setRecentEmojis] = useState<string[]>(() => {
        const saved = localStorage.getItem('recent_emojis');
        return saved ? JSON.parse(saved) : ['👍', '❤️', '😂', '😮', '😢', '🔥'];
    });
    const [showEmojiPickerFor, setShowEmojiPickerFor] = useState<string | null>(null);

    useScrollLock(!!showEmojiPickerFor);
    const [replyingTo, setReplyingTo] = useState<ChannelMessage | null>(null);
    const [isQuickComposerVisible, setIsQuickComposerVisible] = useState(false);
    const [dragOffsets, setDragOffsets] = useState<Record<string, number>>({});
    const dragStartXRef = useRef<Record<string, number>>({});
    const longPressTimeoutRef = useRef<Record<string, NodeJS.Timeout | null>>({});
    const lastReactionTimeRef = useRef<Record<string, number>>({});
    const [deletingMessage, setDeletingMessage] = useState<ChannelMessage | null>(null);
    const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');
    const [typingUsers, setTypingUsers] = useState<Record<string, { username: string, pfp: string }>>({});
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [postCreatorModalOpen, setPostCreatorModalOpen] = useState(false);

    useEffect(() => {
        onInternalModalStateChange?.(!!(showSettings || showMembers || showInviteModal || deletingMessage || activeMenu || showEmojiPickerFor || cropImage || postCreatorModalOpen || isNavigationModalOpen));
    }, [showSettings, showMembers, showInviteModal, deletingMessage, activeMenu, showEmojiPickerFor, cropImage, postCreatorModalOpen, isNavigationModalOpen, onInternalModalStateChange]);

    useEffect(() => {
        if (closeInternalModalsTrigger > 0) {
            setShowSettings(false);
            setShowMembers(false);
            setDeletingMessage(null);
            setActiveMenu(null);
            setShowEmojiPickerFor(null);
            setCropImage(null);
            setShowInviteModal(false);
            setIsNavigationModalOpen(false);
        }
    }, [closeInternalModalsTrigger]);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const MAX_DRAG_DISTANCE = typeof window !== 'undefined' ? window.innerWidth * 0.1 : 50;
    const DRAG_THRESHOLD = 30; // Minimum drag distance to trigger action

    const [defaultChannels] = useState<Channel[]>([
        { id: 'posts', name: 'posts', type: 'posts', order: 0 },
        { id: 'chat', name: 'chat', type: 'chat', order: 1 },
        { id: 'voice', name: 'voice', type: 'voice', order: 2 }
    ]);
    const [draggedChannelId, setDraggedChannelId] = useState<string | null>(null);
    const [dragOverChannelId, setDragOverChannelId] = useState<string | null>(null);

    // Local-only community transparency (0–100), persisted per community in localStorage
    const communityOpacityKey = `communityOpacity_${community?._id || 'default'}`;
    const [communityOpacity, setCommunityOpacity] = useState<number>(() => {
        const saved = localStorage.getItem(communityOpacityKey);
        return saved !== null ? Number(saved) : 50;
    });

    const isMember = community?.members?.some(m => m.toLowerCase() === currentUserId?.toLowerCase());
    const isCreator = community?.creator?.toLowerCase() === currentUserId?.toLowerCase();
    const isAdminOrCreator = community ? (community.creator?.toLowerCase() === currentUserId?.toLowerCase() || community.admins?.some(a => a.toLowerCase() === currentUserId?.toLowerCase())) : false;
    const canViewMembers = isMember && (community?.whoCanViewMembers === 'admins' ? isAdminOrCreator : true);
    const channelsToShow = useMemo(() => {
        const channels = community?.channels && community.channels.length > 0 ? community.channels : defaultChannels;
        return [...channels].sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [community?.channels, defaultChannels]);
    const currentChannel = channelsToShow.find((c: Channel) => c.id === currentChannelId) || channelsToShow[0];

    const activeVoiceUsers = useMemo(() => {
        if (!community || !voiceRooms) return [];
        const communityVoiceRooms = Object.entries(voiceRooms).filter(([key]) => key.startsWith(`${community._id}:`));
        const allUsers: any[] = [];
        communityVoiceRooms.forEach(([key, users]) => {
            const channelId = key.split(':')[1];
            const channel = channelsToShow.find((c: Channel) => c.id === channelId);
            if (users && Array.isArray(users)) {
                users.forEach((u: any) => {
                    allUsers.push({ ...u, channelName: channel?.name || 'Voice' });
                });
            }
        });
        return allUsers;
    }, [community, voiceRooms, channelsToShow]);

    const handleInviteFollower = async (targetUser: string) => {
        if (!community || !currentUserId) return;
        setInvitingUserIds(prev => new Set(prev).add(targetUser));
        try {
            await joinCommunityOnServer(community._id, targetUser, community.inviteLink, currentUserId);
            // Update local community state optimistically
            setCommunity(prev => {
                if (!prev) return null;
                const members = Array.isArray(prev.members) ? prev.members : [];
                return {
                    ...prev,
                    members: members.includes(targetUser) ? members : [...members, targetUser]
                };
            });
        } catch (error) {
            console.error('Failed to invite follower:', error);
        } finally {
            setInvitingUserIds(prev => {
                const next = new Set(prev);
                next.delete(targetUser);
                return next;
            });
        }
    };

    const handleApprovePost = async (postId: number) => {
        if (!community || !currentUserId) return;
        try {
            await approveCommunityPostOnServer(community._id, postId, currentUserId);
            setCommunityPosts(prev => prev.map(p => p.id === postId ? { ...p, isApproved: true } : p));
        } catch (err) {
            console.error('Failed to approve post:', err);
        }
    };

    const weightedFollowers = useMemo(() => {
        if (!currentUserData) return [];
        const friendsList = currentUserData.friends || [];
        const followersList = currentUserData.followers || [];
        const combinedCandidates = [...new Set([...friendsList, ...followersList])];

        if (combinedCandidates.length === 0) return [];

        const sortedIds = getWeightedUserList(
            currentUserId,
            combinedCandidates,
            usersData,
            conversations,
            communities,
            allPosts
        );

        // Filter out those who are already members
        const existingMembers = new Set(community?.members || []);
        return sortedIds.filter(id => !existingMembers.has(id));
    }, [usersData, currentUserId, conversations, communities, allPosts, community?.members, currentUserData]);

    const filteredWeightedFollowers = useMemo(() => {
        if (!inviteSearchTerm.trim()) return weightedFollowers;
        const term = inviteSearchTerm.toLowerCase();
        return weightedFollowers.filter(id => {
            const ud = usersData[id];
            return id.toLowerCase().includes(term) || (ud?.displayName || '').toLowerCase().includes(term);
        });
    }, [weightedFollowers, inviteSearchTerm, usersData]);

    const communityShareUrl = useMemo(() => {
        if (!community) return '';
        const baseOrigin = typeof window !== 'undefined' && ((window as any).Capacitor !== 'undefined' || window.location.hostname === 'localhost') ? 'https://goodlyfe.net' : (typeof window !== 'undefined' ? window.location.origin : '');
        return community.isPrivate
            ? `${baseOrigin}/community/${encodeURIComponent(community.inviteLink || community._id)}`
            : `${baseOrigin}/community/${encodeURIComponent(community.name)}`;
    }, [community]);

    const communityShareTitle = useMemo(() => {
        if (!community) return '';
        return community.isPrivate ? 'Join our private community!' : `Join ${community.name} on GoodLyfe!`;
    }, [community]);

    const communityShareText = useMemo(() => {
        if (!community) return '';
        return `@${currentUserId} invited you to ${community.name} on GoodLyfe! Join here: ${communityShareUrl}`;
    }, [community, currentUserId, communityShareUrl]);

    const communitySharePost = useMemo(() => {
        if (!community) return null;
        return {
            id: community._id,
            author: community._id,
            type: 'profile',
            content: { text: community.bio || '' },
            themeColor: community.themeColor || '#161b22',
            textColor: '#ffffff'
        } as any;
    }, [community]);

    const communityAuthorData = useMemo(() => {
        if (!community) return null;
        return {
            username: community._id,
            displayName: community.name,
            pfp: community.avatar || '',
            profileBg: { url: community.bg?.url || '' },
            profileBgMode: community.bg?.style === 'color' ? 'color' : 'image',
            profileBgSlideshow: community.bgSlideshow || { enabled: false, attachments: [] },
            appThemeColor: community.themeColor || '#161b22',
            appTextColor: '#ffffff',
            homeBg: { url: community.bg?.url || '' },
            homeBgSlideshow: community.bgSlideshow || { enabled: false, attachments: [] },
        } as any;
    }, [community]);

    const handleJoinFirstVoice = () => {
        const firstVoiceChannel = channelsToShow.find((c: Channel) => c.type === 'voice');
        if (firstVoiceChannel) {
            setVoiceChannelToJoin(firstVoiceChannel.id);
        }
    };

    // Detect mobile on resize
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle initial shared content
    useEffect(() => {
        if (initialSharedContent && currentChannelId === 'chat') {
            if (initialSharedContent.text) setMessageInput(initialSharedContent.text);
            if (initialSharedContent.media && initialSharedContent.media.length > 0) {
                const newAttachments: Attachment[] = initialSharedContent.media.map(m => ({
                    url: m.url,
                    type: m.type as any,
                    width: m.width,
                    height: m.height
                }));
                setCurrentAttachments(prev => [...prev, ...newAttachments]);
            }
            if (clearSharedContent) clearSharedContent();
        }
    }, [initialSharedContent, currentChannelId, clearSharedContent]);

    // On mobile: hide sidebar/modal when a channel is selected
    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false);
            setIsNavigationModalOpen(false);
        }
    }, [currentChannelId, isMobile]);

    // Load community + posts
    useEffect(() => {
        const existingCommunity = communities.find(c => c._id === query || c.name === query);
        if (existingCommunity) {
            setCommunity(existingCommunity);
            setIsLocalLoading(false);
        } else {
            setIsLocalLoading(true);
        }

        setLoadError(null);
        fetchCommunityDetails(query).then((commData: Community) => {
            if (commData) {
                setCommunity(commData);
            }
            setIsLocalLoading(false);
        }).catch(err => {
            console.error('[CommunityDetailPage] Error loading community:', err);
            const msg = err?.response?.data?.error || err?.message || 'Failed to load community.';
            if (!existingCommunity) setLoadError(msg);
            setIsLocalLoading(false);
        });
    }, [query, communities]);

    // Update current channel if channelId prop changes
    useEffect(() => {
        if (channelId) {
            // Check if channelId is a type-hint (posts, chat, voice)
            if (['posts', 'chat', 'voice'].includes(channelId) && community) {
                const firstOfType = channelsToShow.find((c: Channel) => c.type === channelId);
                if (firstOfType) {
                    setCurrentChannelId(firstOfType.id);
                    return;
                }
            }
            setCurrentChannelId(channelId);
        }
    }, [channelId, community, channelsToShow]);

    useEffect(() => {
        if (currentChannelId && onChannelChange) {
            onChannelChange(currentChannelId);
        }
    }, [currentChannelId, onChannelChange]);

    // Load messages from server when switching to a chat/voice channel
    useEffect(() => {
        if (!community || currentChannel?.type === 'posts') return;
        fetchChannelMessages(community._id, currentChannelId, currentUserId, query)
            .then((msgs: any[]) => {
                // Normalize server response to ChannelMessage shape
                setChannelMessages(msgs.map(m => ({
                    id: m._id || m.id,
                    channelId: m.channelId,
                    communityId: m.communityId,
                    authorId: m.authorId,
                    text: m.text,
                    media: m.media,
                    replyTo: m.replyTo,
                    reactions: m.reactions, // Server Map is converted to Object by axios/JSON
                    timestamp: m.timestamp
                })));
            })
            .catch(err => console.error('[CommunityDetailPage] Error fetching messages:', err));
    }, [community, currentChannelId, query, currentUserId]);

    // Load posts from server when switching to a posts channel
    useEffect(() => {
        if (!community || currentChannel?.type !== 'posts') return;
        fetchCommunityPosts(community._id, currentUserId, query, currentChannelId)
            .then((postsData: HydratedPost[]) => {
                if (Array.isArray(postsData)) {
                    setCommunityPosts(postsData);
                } else {
                    setCommunityPosts([]);
                }
            })
            .catch(err => console.error('[CommunityDetailPage] Error fetching posts for channel:', err));
    }, [community, currentChannelId, query, currentUserId]);

    // Scroll to message if passed via deep link
    useEffect(() => {
        if (messageId && messageId !== hasScrolledToMessage && channelMessages.some(m => m.id === messageId)) {
            setTimeout(() => {
                scrollToMessage(messageId);
                setHasScrolledToMessage(messageId);
            }, 100);
        }
    }, [messageId, channelMessages, hasScrolledToMessage]);

    // Handle arrow key navigation for message selection
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if (activeMenu || showEmojiPickerFor || deletingMessage) return;
            if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;

            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                setChannelMessages(prevMsgs => {
                    const sorted = [...prevMsgs].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                    const currentIndex = sorted.findIndex(m => m.id === selectedMessageId);

                    let nextIndex = currentIndex;
                    if (e.key === 'ArrowUp') {
                        nextIndex = currentIndex === -1 ? sorted.length - 1 : Math.max(0, currentIndex - 1);
                    } else {
                        nextIndex = currentIndex === -1 ? 0 : Math.min(sorted.length - 1, currentIndex + 1);
                    }

                    const nextMsg = sorted[nextIndex];
                    if (nextMsg) {
                        setSelectedMessageId(nextMsg.id);
                        // scrollToMessage is now hoisted if declared as a function
                        scrollToMessage(nextMsg.id);
                    }
                    return prevMsgs;
                });
            } else if (e.key === 'Delete' && selectedMessageId) {
                const msg = channelMessages.find(m => m.id === selectedMessageId);
                if (msg) {
                    const isMine = msg.authorId === currentUserId;
                    if (isMine || isAdminOrCreator) {
                        setDeletingMessage(msg);
                    }
                }
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [selectedMessageId, activeMenu, showEmojiPickerFor, deletingMessage, channelMessages]);

    // Socket listeners for real-time updates
    useEffect(() => {
        if (!socket || !community) return;

        const channelRoom = `community-channel:${community._id}:${currentChannelId}`;
        socket.emit('join-room', channelRoom);

        const handleMessage = (msg: any) => {
            if (msg.channelId === currentChannelId) {
                setChannelMessages(prev => {
                    const exists = prev.some(m => m.id === (msg._id || msg.id));
                    if (exists) return prev;
                    return [...prev, { ...msg, id: msg._id || msg.id }];
                });
            }
        };

        const handleMessageUpdated = (msg: any) => {
            if (msg.channelId === currentChannelId) {
                setChannelMessages(prev => prev.map(m => (m.id === (msg._id || msg.id) ? { ...msg, id: msg._id || msg.id } : m)));
            }
        };

        const handleMessageDeleted = ({ messageId, channelId }: { messageId: string, channelId: string }) => {
            if (channelId === currentChannelId) {
                setChannelMessages(prev => prev.filter(m => m.id !== messageId));
            }
        };

        const handleTypingStatus = ({ room, username, isTyping, pfp }: { room: string, username: string, isTyping: boolean, pfp: string }) => {
            const currentRoom = `community-channel:${community._id}:${currentChannelId}`;
            if (room === currentRoom && username !== currentUserId) {
                setTypingUsers(prev => {
                    const next = { ...prev };
                    if (isTyping) {
                        next[username] = { username, pfp };
                    } else {
                        delete next[username];
                    }
                    return next;
                });
            }
        };

        socket.on('channel-message', handleMessage);
        socket.on('channel-message-updated', handleMessageUpdated);
        socket.on('channel-message-deleted', handleMessageDeleted);
        socket.on('typing-status', handleTypingStatus);

        const handleNewPost = (newPost: Post) => {
            if (newPost.communityId !== community._id) return;
            setCommunityPosts(prev => {
                if (prev.some(p => p.id === newPost.id)) return prev;
                const optimisticMatch = prev.find(p =>
                    p.id > 1000000000000 &&
                    p.author === newPost.author &&
                    p.type === newPost.type &&
                    (p.content.text === newPost.content.text || (p.content.media?.length === newPost.content.media?.length))
                );
                const [hydrated] = getHydratedPosts([newPost]);
                if (optimisticMatch) return prev.map(p => p.id === optimisticMatch.id ? hydrated : p);
                return [hydrated, ...prev];
            });
        };

        const handlePostUpdated = (updatedPost: Post) => {
            setCommunityPosts(prev => prev.map(p => p.id === updatedPost.id ? { ...p, ...updatedPost } : p));
        };

        const handlePostDeleted = (postId: number) => {
            setCommunityPosts(prev => prev.filter(p => p.id !== postId));
        };

        socket.on('new-post', handleNewPost);
        socket.on('post-updated', handlePostUpdated);
        socket.on('post-deleted', handlePostDeleted);

        return () => {
            socket.emit('leave-room', channelRoom);
            socket.off('channel-message', handleMessage);
            socket.off('channel-message-updated', handleMessageUpdated);
            socket.off('channel-message-deleted', handleMessageDeleted);
            socket.off('typing-status', handleTypingStatus);
            socket.off('post-updated', handlePostUpdated);
            socket.off('post-deleted', handlePostDeleted);
            socket.off('new-post', handleNewPost);
        };
    }, [socket, community, currentChannelId, currentUserId]);

    // Emit typing status
    const emitTypingStatus = (isTyping: boolean) => {
        if (!socket || !community) return;
        const room = `community-channel:${community._id}:${currentChannelId}`;
        const pfp = usersData[currentUserId]?.pfp || '';
        socket.emit('typing-status', { room, username: currentUserId, isTyping, pfp });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setMessageInput(value);

        // Auto-resize
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
        }

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

        emitTypingStatus(value.length > 0);

        if (value.length > 0) {
            typingTimeoutRef.current = setTimeout(() => {
                emitTypingStatus(false);
            }, 3000);
        }

        // Mention detection
        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPosition);
        const match = textBeforeCursor.match(/(^|\s)@([a-zA-Z0-9_\-]*)$/);

        if (match) {
            setMentionSearchTerm(match[2]);
            setMentionCursorIndex(cursorPosition - match[2].length); // Index right after the '@'
            setShowMentionDropdown(true);
        } else {
            setShowMentionDropdown(false);
        }
    };

    const suggestedMentions = useMemo(() => {
        if (!community || !showMentionDropdown) return [];

        const term = mentionSearchTerm.toLowerCase();

        // 1. Get recent participants in current channel
        const recentAuthors = Array.from(new Set([...channelMessages].map(m => m.authorId).reverse()));

        // 2. All community members
        const allMembers = community.members;

        // 3. Special chat tag
        const allOptions = ['chat'];

        // Add recent first
        for (const author of recentAuthors) {
            if (author !== currentUserId && !allOptions.includes(author)) {
                allOptions.push(author);
            }
        }

        // Add remaining
        for (const member of allMembers) {
            if (member !== currentUserId && !allOptions.includes(member)) {
                allOptions.push(member);
            }
        }

        // Filter by search term
        return allOptions.filter(opt => {
            if (opt === 'chat') return opt.includes(term);
            const user = usersData[opt];
            if (!user) return opt.toLowerCase().includes(term);
            return opt.toLowerCase().includes(term) || (user.displayName && user.displayName.toLowerCase().includes(term));
        }).slice(0, 10); // Limit to top 10

    }, [community, showMentionDropdown, mentionSearchTerm, channelMessages, usersData, currentUserId]);

    // Reset selection when list changes
    useEffect(() => {
        setSelectedMentionIndex(0);
    }, [suggestedMentions]);

    const handleMentionSelect = (username: string) => {
        const before = messageInput.substring(0, mentionCursorIndex);
        const after = messageInput.substring(textareaRef.current?.selectionStart || mentionCursorIndex + mentionSearchTerm.length);
        const newValue = `${before}${username} ${after}`;
        setMessageInput(newValue);
        setShowMentionDropdown(false);

        // Keep focus and set cursor
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newCursorPos = mentionCursorIndex + username.length + 1;
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);
    };

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [channelMessages]);

    // Click outside listener for menu
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (activeMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setActiveMenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeMenu]);

    // if the community hasn't loaded yet, show a spinner/placeholder to avoid
    // a lot of "community is possibly null" TypeScript errors throughout the
    // render block.  the UI will re-render once the state is populated.
    if (isGlobalLoading || isLocalLoading) {
        return (
            <div className="flex-1 flex items-center justify-center p-8 text-gray-400">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span>Loading community...</span>
                </div>
            </div>
        );
    }

    if (loadError || !community) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="text-red-400 font-semibold mb-2">Could not load community</p>
                    <p className="text-gray-500 text-sm">{loadError || 'Community not found.'}</p>
                </div>
            </div>
        );
    }

    const handlePfpFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && currentUserId) {
            const reader = new FileReader();
            reader.onload = () => {
                setCropImage(reader.result as string);
                setIsCropping(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = async (croppedBlob: Blob) => {
        if (!currentUserId) return;
        try {
            const fileToUpload = new File([croppedBlob], `community-pfp-${Date.now()}.png`, { type: 'image/png' });
            let url: string;
            if (isDemo) {
                url = URL.createObjectURL(fileToUpload);
            } else {
                url = await uploadMediaFile(fileToUpload, currentUserId);
            }
            updateCommunityField({ avatar: url });
            setIsCropping(false);
            setCropImage(null);
        } catch (e) {
            console.error("Failed to update community picture", e);
            alert("Failed to update community picture.");
        }
    };

    const handleBgFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && currentUserId) {
            try {
                const compressedBlob = await compressImage(file);
                const fileToUpload = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                let url: string;
                if (isDemo) {
                    url = URL.createObjectURL(fileToUpload);
                } else {
                    url = await uploadMediaFile(fileToUpload, currentUserId);
                }
                setSettingsForm(prev => ({ ...prev, bg: { ...prev.bg, url: url } }));
            } catch (e) {
                console.error("Failed to update background", e);
                alert("Failed to update background.");
            }
        }
    };

    const handleJoinLeave = async () => {
        if (!community) return;
        if (isDemo) {
            alert("Interactions are disabled in Play Around mode. Log in to join communities!");
            return;
        }
        try {
            if (isMember) {
                await leaveCommunityOnServer(community._id, currentUserId);
                setCommunity(prev => prev ? { ...prev, members: prev.members.filter(m => m !== currentUserId) } : null);
            } else {
                await joinCommunityOnServer(community._id, currentUserId, query);
                setCommunity(prev => prev ? { ...prev, members: [...prev.members, currentUserId] } : null);
            }
        } catch (error: any) {
            alert(error.response?.data?.error || 'Operation failed');
        }
    };

    const handleOpenSettings = () => {
        if (!community) return;
        setSettingsForm({
            name: community.name,
            bio: community.bio || '',
            isPrivate: community.isPrivate,
            requireApprovalForPosts: community.requireApprovalForPosts || false,
            whoCanPost: community.whoCanPost,
            whoCanViewMembers: community.whoCanViewMembers || 'all',
            avatar: community.avatar || '',
            themeColor: community.themeColor || '#161b22',
            bg: community.bg || { url: '', style: 'color' },
            bgResolution: community.bgResolution || 100,
            channels: community.channels || [...defaultChannels],
            bgMode: (community as any).bgMode || (community.bg?.style === 'color' ? 'color' : 'image'),
            bgSlideshow: (community as any).bgSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' }
        });
        if (community.bg?.style === 'color' && community.bg.url) {
            setLocalBgColor(community.bg.url);
        }
        setShowSettings(true);
    };

    const updateCommunityField = async (fields: any) => {
        if (!community) return;
        if (isDemo) {
            alert("Interactions are disabled in Play Around mode. Log in to manage communities!");
            return;
        }

        const newRequireApproval = fields.requireApprovalForPosts !== undefined ? fields.requireApprovalForPosts : settingsForm.requireApprovalForPosts;
        const updatedForm = {
            ...settingsForm,
            ...(typeof fields === 'function' ? fields(settingsForm) : fields),
            requireApprovalForPosts: newRequireApproval
        };

        setSettingsForm(updatedForm);
        setCommunity(prev => prev ? { ...prev, ...updatedForm } : null);

        try {
            const updated = await updateCommunityOnServer(community._id, { ...updatedForm, requestingUser: currentUserId });
            setCommunity(updated);
            if (onUpdateCommunity) onUpdateCommunity(updated);
        } catch (error: any) {
            console.error("Failed to update community settings:", error);
        }
    };

    const handleCommunityBgUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0 || !community) return;

        const currentSlideshow = settingsForm.bgSlideshow || { enabled: true, attachments: [] as any[], intervalValue: 10, intervalUnit: 'seconds' };
        const newAttachments = [...(currentSlideshow.attachments || [])] as any[];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const isVideo = file.type.startsWith('video/');
            const tempUrl = URL.createObjectURL(file) + (isVideo ? "#video" : "");
            const tempId = `temp-${Date.now()}-${i}`;
            
            newAttachments.push({
                id: tempId,
                url: tempUrl,
                type: isVideo ? 'video' : 'image',
                name: file.name
            } as any);
        }

        const tempSlideshow = { ...currentSlideshow, attachments: newAttachments };
        setSettingsForm(prev => ({ ...prev, bgSlideshow: tempSlideshow, bg: { ...prev.bg, url: newAttachments[0]?.url || '', style: prev.bg.style || 'cover' } }));

        try {
            const uploadedAttachments = [...(currentSlideshow.attachments || [])] as any[];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const isVideo = file.type.startsWith('video/');
                const isPreservedType = isVideo || ['image/gif', 'image/webp', 'image/apng'].includes(file.type) || /\.(gif|webp|mp4|webm|ogg|mov|m4v)$/i.test(file.name);
                let fileToUpload = file;
                if (!isPreservedType) {
                    try {
                        const compressedBlob = await compressImage(file);
                        fileToUpload = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                    } catch (e) {
                        console.error("Compression failed", e);
                    }
                }
                const url = await uploadMediaFile(fileToUpload, currentUserId);
                if (url) {
                    uploadedAttachments.push({
                        id: `attachment-${Date.now()}-${i}`,
                        url: url,
                        type: isVideo ? 'video' : 'image',
                        name: file.name
                    } as any);
                }
            }
            const finalSlideshow = { ...currentSlideshow, attachments: uploadedAttachments };
            updateCommunityField({
                bgSlideshow: finalSlideshow,
                bg: {
                    url: uploadedAttachments[0]?.url || '',
                    style: settingsForm.bg.style || 'cover'
                }
            });
        } catch (err) {
            console.error("Multiple upload failed", err);
        }
    };

    const handleCommunitySlideshowChange = (slideshow: any) => {
        updateCommunityField({
            bgSlideshow: slideshow,
            bg: {
                url: slideshow.attachments?.[0]?.url || '',
                style: settingsForm.bg.style || 'cover'
            }
        });
    };

    const handleSaveSettings = async (e: React.FormEvent) => {
        e.preventDefault();
        setShowSettings(false);
    };

    const handleAddChannel = (type: ChannelType) => {
        const id = `${type}-${Date.now()}`;
        const name = type === 'posts' ? 'new-posts' : type === 'chat' ? 'new-chat' : 'new-voice';
        updateCommunityField({
            channels: [...settingsForm.channels, { id, name, type }]
        });
    };

    const handleRemoveChannel = (channelId: string) => {
        const channelToRemove = settingsForm.channels.find(c => c.id === channelId);
        if (!channelToRemove) return;

        // Count channels of this type
        const typeCount = settingsForm.channels.filter(c => c.type === channelToRemove.type).length;
        if (typeCount <= 1) {
            alert(`You must have at least one ${channelToRemove.type} channel.`);
            return;
        }

        const newChannels = settingsForm.channels.filter(c => c.id !== channelId);
        updateCommunityField({ channels: newChannels });

        // If we removed the currently selected channel, reset to something else
        if (currentChannelId === channelId) {
            setCurrentChannelId(newChannels[0].id);
        }
    };

    const handleRenameChannel = (channelId: string, newName: string) => {
        setSettingsForm(prev => ({
            ...prev,
            channels: prev.channels.map(c => c.id === channelId ? { ...c, name: newName.toLowerCase().replace(/\s+/g, '-') } : c)
        }));
    };

    const handleChannelNotifChange = async (channelId: string, mode: string, throttleMinutes?: number) => {
        if (!community) return;
        try {
            const key = `${community._id}:${channelId}`;
            const currentUserData = usersData[currentUserId] || {};
            const currentSettings = currentUserData.channelNotificationSettings || {};
            const newSettings = {
                ...currentSettings,
                [key]: { mode, throttleMinutes }
            };

            updateUserData?.((prev: Record<string, UserData>) => {
                const user = prev[currentUserId];
                if (!user) return prev;
                return {
                    ...prev,
                    [currentUserId]: {
                        ...user,
                        channelNotificationSettings: newSettings
                    }
                };
            });

            await updateUserOnServer(currentUserId, { channelNotificationSettings: newSettings });
        } catch (error) {
            console.error('[CommunityDetailPage] Failed to update channel notification settings:', error);
        }
    };

    const renderChannelNotifSettings = () => {
        const handleAddToHomescreen = () => {
            if (Capacitor.isNativePlatform()) {
                alert('On the Native App, manage Quick Actions in Global Settings!');
                return;
            }
            const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
            if (isIos) {
                alert('To add this community to your home screen: tap the Share icon at the bottom of your Safari browser, then tap "Add to Home Screen".');
            } else if ((window as any).deferredPrompt) {
                (window as any).deferredPrompt.prompt();
                (window as any).deferredPrompt.userChoice.then(() => {
                    (window as any).deferredPrompt = null;
                });
            } else {
                alert('To add to home screen, use your browser menu and select "Add to Home screen" or "Install App".');
            }
        };


        if (!community) return null;
        return (
            <div className="border-t border-white/10 pt-4 mt-4 text-left">
                <label className="text-[10px] font-black uppercase tracking-tight text-gray-400 mb-3 block">Channel Notification Settings</label>
                <div className="space-y-4">
                    {channelsToShow.map((ch: Channel) => {
                        const key = `${community._id}:${ch.id}`;
                        const currentUserData = usersData[currentUserId] || {};
                        const setting = currentUserData.channelNotificationSettings?.[key] || {
                            mode: ch.type === 'voice' ? 'default' : 'all',
                            throttleMinutes: ch.type === 'voice' ? 15 : 5
                        };
                        return (
                            <div key={ch.id} className="bg-black/30 p-3 rounded-xl border border-white/5 space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-400">
                                            {ch.type === 'voice' ? (
                                                <PhoneIcon className="w-4 h-4" />
                                            ) : (
                                                <HashtagIcon className="w-4 h-4" />
                                            )}
                                        </span>
                                        <span className="text-sm font-bold text-white">#{ch.name}</span>
                                        <span className="text-[9px] font-black uppercase tracking-wider text-gray-500 bg-white/5 px-2 py-0.5 rounded">
                                            {ch.type}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <select
                                        value={setting.mode}
                                        onChange={(e) => handleChannelNotifChange(ch.id, e.target.value, setting.throttleMinutes)}
                                        className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                                    >
                                        {ch.type === 'chat' && (
                                            <>
                                                <option value="all">All Messages (Default)</option>
                                                <option value="throttle">Throttle (1 notif per X mins)</option>
                                                <option value="mentions">Mentions Only</option>
                                                <option value="none">None</option>
                                            </>
                                        )}
                                        {ch.type === 'posts' && (
                                            <>
                                                <option value="all">All Posts</option>
                                                <option value="followed">Posts from followed users</option>
                                                <option value="mentions">Mentions Only</option>
                                                <option value="none">None</option>
                                            </>
                                        )}
                                        {ch.type === 'voice' && (
                                            <>
                                                <option value="default">Default (15 mins)</option>
                                                <option value="throttle">Throttle (1 notif per X mins)</option>
                                                <option value="none">None</option>
                                            </>
                                        )}
                                    </select>

                                    {setting.mode === 'throttle' && (
                                        <div className="flex items-center gap-2 animate-fade-in pl-1">
                                            <span className="text-[10px] text-gray-400">Throttle limit (minutes):</span>
                                            <input
                                                type="number"
                                                min="1"
                                                max="1440"
                                                value={setting.throttleMinutes || (ch.type === 'voice' ? 15 : 5)}
                                                onChange={(e) => {
                                                    const mins = parseInt(e.target.value) || 1;
                                                    handleChannelNotifChange(ch.id, 'throttle', mins);
                                                }}
                                                className="w-16 bg-black/50 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500 text-center"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {!Capacitor.isNativePlatform() && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={handleAddToHomescreen}
                            className="w-full py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-bold hover:brightness-110 transition-all border border-white/10 flex items-center justify-center gap-2 shadow-lg"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                            Add to Homescreen
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const handleGrantAdmin = async (targetUser: string) => {
        if (!community) return;
        if (isDemo) {
            alert("Interactions are disabled in Play Around mode.");
            return;
        }
        setMemberActionLoading(targetUser + '_grant');
        try {
            const updated = await grantAdminOnServer(community._id, currentUserId, targetUser);
            setCommunity(updated);
        } catch (error: any) {
            alert(error.response?.data?.error || 'Failed to grant admin');
        } finally {
            setMemberActionLoading(null);
        }
    };

    const handleRevokeAdmin = async (targetUser: string) => {
        if (!community) return;
        if (isDemo) {
            alert("Interactions are disabled in Play Around mode.");
            return;
        }
        setMemberActionLoading(targetUser + '_revoke');
        try {
            const updated = await revokeAdminOnServer(community._id, currentUserId, targetUser);
            setCommunity(updated);
        } catch (error: any) {
            alert(error.response?.data?.error || 'Failed to revoke admin');
        } finally {
            setMemberActionLoading(null);
        }
    };

    const handleRemoveMember = async (targetUser: string) => {
        if (!community) return;
        if (isDemo) {
            alert("Interactions are disabled in Play Around mode.");
            return;
        }
        if (!window.confirm(`Remove @${targetUser} from this community?`)) return;
        setMemberActionLoading(targetUser + '_remove');
        try {
            const updated = await removeMemberOnServer(community._id, currentUserId, targetUser);
            setCommunity(updated);
        } catch (error: any) {
            alert(error.response?.data?.error || 'Failed to remove member');
        } finally {
            setMemberActionLoading(null);
        }
    };


    const isPhoneCommunity = typeof window !== 'undefined' && window.innerWidth <= 1024;

    const handlePaste = async (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const files: File[] = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                const file = items[i].getAsFile();
                if (file && (file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/'))) {
                    files.push(file);
                }
            }
        }

        if (files.length > 0) {
            e.preventDefault();

            const totalSize = files.reduce((sum, file) => sum + file.size, 0);
            if (totalSize > 500 * 1024 * 1024) {
                alert('File size exceeds the 500MB limit. Please choose smaller files.');
                return;
            }

            for (const file of files) {
                try {
                    let processedFile = file;
                    let mediaType: MediaType = 'image';

                    if (file.type.startsWith('video/')) {
                        mediaType = 'video';
                        const blob = await compressVideo(file);
                        processedFile = new File([blob], file.name, { type: file.type });
                    } else if (file.type.startsWith('audio/')) {
                        mediaType = 'audio';
                        const blob = await compressAudio(file);
                        processedFile = new File([blob], file.name, { type: file.type });
                    } else {
                        const blob = await compressImage(file);
                        processedFile = new File([blob], file.name, { type: file.type });
                    }

                    const attachment: Attachment = {
                        url: URL.createObjectURL(processedFile),
                        type: mediaType,
                        file: processedFile
                    };
                    setCurrentAttachments(prev => [...prev, attachment]);
                } catch (err) {
                    console.error('Error pasting file:', err);
                }
            }
        }
    };

    const handleInviteClick = async () => {
        if (!community) return;
        const invitePath = community.inviteLink || community._id;
        const inviteUrl = `${window.location.origin}/community/${encodeURIComponent(invitePath)}`;
        const title = `Invite to ${community.name}!`;
        try {
            if (isPhoneCommunity && (navigator as any).share) {
                await (navigator as any).share({ title, text: title, url: inviteUrl });
                setInviteFeedback('Shared');
            } else if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(inviteUrl);
                setInviteFeedback('Copied');
            } else {
                window.prompt('Copy invite link', inviteUrl);
                setInviteFeedback('Copied');
            }
        } catch (e) {
            setInviteFeedback(null);
        }
        setTimeout(() => setInviteFeedback(null), 2000);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > 500 * 1024 * 1024) {
            alert('File size exceeds the 500MB limit. Please choose smaller files.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        const newAttachments: Attachment[] = files.map(file => ({
            url: URL.createObjectURL(file),
            type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'audio',
            file
        }));

        setCurrentAttachments(prev => [...prev, ...newAttachments]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeAttachment = (index: number) => {
        setCurrentAttachments(prev => {
            const newAtts = [...prev];
            URL.revokeObjectURL(newAtts[index].url);
            newAtts.splice(index, 1);
            return newAtts;
        });
    };

    const handleVoiceMessageComplete = async (audioBlob: Blob) => {
        if (isDemo) {
            alert("Voice messaging is disabled in Play Around mode.");
            return;
        }
        setIsRecording(false);
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            setAudioStream(null);
        }
        let extension = 'webm';
        let mimeType = audioBlob.type || 'audio/webm';
        if (audioBlob.type) {
            if (audioBlob.type.includes('mp4')) {
                extension = 'm4a';
                mimeType = 'audio/mp4';
            } else if (audioBlob.type.includes('aac')) {
                extension = 'aac';
                mimeType = 'audio/aac';
            } else if (audioBlob.type.includes('ogg')) {
                extension = 'ogg';
                mimeType = 'audio/ogg';
            } else if (audioBlob.type.includes('wav')) {
                extension = 'wav';
                mimeType = 'audio/wav';
            } else if (audioBlob.type.includes('webm')) {
                extension = 'webm';
                mimeType = 'audio/webm';
            }
        } else {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            if (isIOS) {
                extension = 'm4a';
                mimeType = 'audio/mp4';
            }
        }
        const url = URL.createObjectURL(audioBlob);
        const attachment: Attachment = {
            url,
            type: 'audio',
            file: new File([audioBlob], `voice-message-${Date.now()}.${extension}`, { type: mimeType })
        };
        setCurrentAttachments(prev => [...prev, attachment]);
    };

    const handleCameraCapture = (file: File) => {
        const pseudoEvent = {
            target: {
                files: [file]
            }
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleFileSelect(pseudoEvent);
    };

    const handleAttachTenorGif = (gifUrl: string) => {
        const newAttachment: Attachment = {
            url: gifUrl,
            type: 'image'
        };
        setCurrentAttachments(prev => [...prev, newAttachment]);
        setShowTenorModal(false);
    };

    const handleGalleryClick = async () => {
        if (Capacitor.isNativePlatform()) {
            try {
                const checkStatus = await Camera.checkPermissions();
                if (checkStatus.photos !== 'granted' && checkStatus.photos !== 'limited') {
                    const reqStatus = await Camera.requestPermissions({ permissions: ['photos'] });
                    if (reqStatus.photos !== 'granted' && reqStatus.photos !== 'limited') {
                        throw new Error('Photo library permission denied');
                    }
                }
                const result = await Camera.pickImages({
                    quality: 100,
                    limit: 10
                });
                if (result.photos && result.photos.length > 0) {
                    const files = await Promise.all(result.photos.map(async (p, idx) => {
                        const response = await fetch(p.webPath!);
                        const blob = await response.blob();
                        const extension = p.format || 'jpeg';
                        const filename = `gallery-${Date.now()}-${idx}.${extension}`;
                        return new File([blob], filename, { type: blob.type || `image/${extension}` });
                    }));
                    const pseudoEvent = {
                        target: {
                            files: files
                        }
                    } as unknown as React.ChangeEvent<HTMLInputElement>;
                    handleFileSelect(pseudoEvent);
                }
            } catch (err) {
                console.error("Native gallery pick failed, falling back to file input", err);
                fileInputRef.current?.click();
            }
        } else {
            fileInputRef.current?.click();
        }
    };

    function scrollToMessage(msgId: string) {
        const element = document.getElementById(`message-${msgId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('bg-blue-500/20');
            setTimeout(() => {
                element.classList.remove('bg-blue-500/20');
            }, 2000);
        }
    }

    const handleSendMessage = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (isDemo) {
            alert("Messaging in communities is disabled in Play Around mode. Log in to start yappin!");
            return;
        }
        if (!community) { console.warn('[Send] No community loaded'); return; }
        const text = messageInput.trim();
        if (!text && currentAttachments.length === 0) { console.warn('[Send] Empty message'); return; }
        if (isSendingMessage || isUploading) { console.warn('[Send] Already sending or uploading'); return; }

        setSendError(null);
        setIsSendingMessage(true);

        try {
            const optimisticId = `local-${Date.now()}`;
            const optimisticMedia: MediaAttachmentType[] = currentAttachments.map(att => ({
                url: att.url,
                type: att.type as any,
                name: att.file?.name
            }));

            // Optimistic update
            const optimisticMsg: ChannelMessage = {
                id: optimisticId,
                channelId: currentChannelId,
                communityId: community._id,
                authorId: currentUserId,
                text,
                media: optimisticMedia.length > 0 ? optimisticMedia : undefined,
                timestamp: new Date().toISOString()
            };

            setChannelMessages(prev => [...prev, optimisticMsg]);
            setMessageInput('');
            const attachmentsToUpload = [...currentAttachments];
            setCurrentAttachments([]);
            setReplyingTo(null);

            let uploadedMedia: MediaAttachmentType[] | undefined = undefined;

            if (attachmentsToUpload.length > 0) {
                setIsUploading(true);
                const filesToUpload = attachmentsToUpload.map(att => ({
                    file: att.file!,
                    type: att.type,
                    name: att.file?.name
                }));

                await new Promise<void>((resolve, reject) => {
                    startGenericUpload(currentUserId, filesToUpload, (attachments) => {
                        uploadedMedia = (attachments as Attachment[]).map(att => ({
                            url: att.url,
                            type: att.type,
                            name: att.file?.name,
                            width: att.width,
                            height: att.height,
                            duration: att.duration
                        }));
                        resolve();
                    }).catch(reject);
                });
                setIsUploading(false);
            }

            const savedMsg = await sendChannelMessage(community._id, currentChannelId, currentUserId, text, uploadedMedia, replyingTo ? { id: replyingTo.id, text: replyingTo.text, authorId: replyingTo.authorId } : undefined);

            // Replace optimistic message
            const normalized: ChannelMessage = {
                id: savedMsg._id || savedMsg.id,
                channelId: savedMsg.channelId,
                communityId: savedMsg.communityId,
                authorId: savedMsg.authorId,
                text: savedMsg.text,
                media: savedMsg.media,
                replyTo: savedMsg.replyTo,
                reactions: savedMsg.reactions,
                timestamp: savedMsg.timestamp
            };
            setChannelMessages(prev => prev.map(m => m.id === optimisticId ? normalized : m));
        } catch (err: any) {
            console.error('[CommunityDetailPage] Error sending message:', err);
            const errMsg = err?.response?.data?.error || err?.message || 'Failed to send. Server may need a restart.';
            setSendError(errMsg);
            // Optionally remove optimistic message on failure
        } finally {
            setIsSendingMessage(false);
            setIsUploading(false);
            emitTypingStatus(false); // Ensure typing status is reset after sending
        }
    };

    const handleSaveEdit = async (messageId: string) => {
        if (!community || !currentUserId || !editText.trim()) return;
        try {
            const updatedMsg = await editChannelMessage(community._id, currentChannelId, messageId, currentUserId, editText);
            setChannelMessages(prev => prev.map(m => m.id === messageId ? { ...m, text: updatedMsg.text, lastEdited: updatedMsg.lastEdited } : m));
            setEditingMessageId(null);
            setEditText('');
        } catch (err: any) {
            console.error('Failed to edit message:', err);
            const errMsg = err.response?.data?.error || err.message || 'Failed to edit message.';
            alert(`Failed: ${errMsg}`);
        }
    };

    const handleMessageKeyDown = (e: React.KeyboardEvent) => {
        if (showMentionDropdown && suggestedMentions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedMentionIndex(prev => Math.min(prev + 1, suggestedMentions.length - 1));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedMentionIndex(prev => Math.max(prev - 1, 0));
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                handleMentionSelect(suggestedMentions[selectedMentionIndex]);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setShowMentionDropdown(false);
                return;
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            // desktop-only change: enter to send
            const isDesktop = window.innerWidth > 768;
            if (isDesktop) {
                e.preventDefault();
                console.log('[CommunityChat] Enter pressed, triggering form submit');
                handleSendMessage();
            }
        }
    };

    const handleDragStart = (e: React.DragEvent, id: string) => {
        if (!isAdminOrCreator) return;
        setDraggedChannelId(id);
        e.dataTransfer.setData('text/plain', id);
        e.dataTransfer.effectAllowed = 'move';

        // Create an transparent image as drag image to avoid default preview if needed
        // const img = new Image();
        // img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
        // e.dataTransfer.setDragImage(img, 0, 0);
    };

    const handleDragOver = (e: React.DragEvent, id: string) => {
        if (!isAdminOrCreator) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (id !== draggedChannelId) {
            setDragOverChannelId(id);
        }
    };

    const handleDrop = async (e: React.DragEvent, targetId: string) => {
        if (!isAdminOrCreator || !draggedChannelId || draggedChannelId === targetId) return;
        if (!community) return;

        // If we are in settings modal, handle differently
        if (showSettings) {
            const currentIndex = settingsForm.channels.findIndex(c => c.id === draggedChannelId);
            const targetIndex = settingsForm.channels.findIndex(c => c.id === targetId);
            if (currentIndex !== -1 && targetIndex !== -1) {
                const newChannels = [...settingsForm.channels];
                const [removed] = newChannels.splice(currentIndex, 1);
                newChannels.splice(targetIndex, 0, removed);
                updateCommunityField({ channels: newChannels });
            }
            setDraggedChannelId(null);
            setDragOverChannelId(null);
            return;
        }

        const newChannels = community.channels ? [...community.channels] : [];
        const draggedIdxInFull = newChannels.findIndex(c => c.id === draggedChannelId);
        const targetIdxInFull = newChannels.findIndex(c => c.id === targetId);

        if (draggedIdxInFull === -1 || targetIdxInFull === -1) return;

        const [removed] = newChannels.splice(draggedIdxInFull, 1);
        newChannels.splice(targetIdxInFull, 0, removed);

        // Update local orders
        const updatedChannels = newChannels.map((c, i) => ({ ...c, order: i }));

        // Optimistically update community
        if (community) {
            setCommunity({ ...community, channels: updatedChannels });

            try {
                // Send the full updated channels list to ensure perfect sync
                await reorderChannels(community._id, updatedChannels);
                console.log('[CommunityChat] Channels reordered successfully');
            } catch (err) {
                console.error('[CommunityChat] Failed to save channel order:', err);
                // Revert on error
                fetchCommunityDetails(query).then(setCommunity);
            }
        }

        setDraggedChannelId(null);
        setDragOverChannelId(null);
    };

    const handleDragEnd = () => {
        setDraggedChannelId(null);
        setDragOverChannelId(null);
    };

    const dataUrlToBlob = (dataUrl: string) => {
        const [header, base64] = dataUrl.split(',');
        const mimeMatch = header.match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/png';
        const binary = atob(base64);
        const array = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
            array[i] = binary.charCodeAt(i);
        }
        return new Blob([array], { type: mime });
    };

    const captureCommunityShareBlob = async (): Promise<Blob | null> => {
        if (!communityShareRef.current) return null;
        if (shareCommunityBlob) return shareCommunityBlob;

        setShareCommunityGenerating(true);
        try {
            const dataUrl = await toPng(communityShareRef.current, {
                cacheBust: true,
                backgroundColor: 'transparent'
            });
            const blob = dataUrlToBlob(dataUrl);
            setShareCommunityBlob(blob);
            return blob;
        } catch (primaryError) {
            try {
                const canvas = await Promise.race([
                    html2canvas(communityShareRef.current, {
                        backgroundColor: null,
                        useCORS: true,
                        scale: 1
                    }),
                    new Promise<HTMLCanvasElement>((_, reject) => setTimeout(() => reject(new Error('html2canvas timeout')), 20000))
                ]);
                const blob = dataUrlToBlob(canvas.toDataURL('image/png'));
                setShareCommunityBlob(blob);
                return blob;
            } catch (fallbackError) {
                console.error('Community share card capture failed', primaryError, fallbackError);
                return null;
            }
        } finally {
            setShareCommunityGenerating(false);
        }
    };

    const handleShareCommunity = () => {
        setShowShareCommunityModal(true);
        setShareCommunityBlob(null);
        setShowCommunityQRCode(false);
    };

    const handleShareCommunityAction = async () => {
        if (!community) return;
        const shareUrl = communityShareUrl;
        const shareData = {
            title: communityShareTitle,
            text: communityShareText,
            url: shareUrl
        };

        try {
            const blob = await captureCommunityShareBlob();
            if (Capacitor.isNativePlatform() && blob) {
                const reader = new FileReader();
                reader.onloadend = async () => {
                    const base64 = (reader.result as string).split(',')[1];
                    const fileName = `goodlyfe_community_${Date.now()}.png`;
                    try {
                        const savedFile = await Filesystem.writeFile({
                            path: fileName,
                            data: base64,
                            directory: Directory.Cache
                        });
                        await Share.share({
                            title: shareData.title,
                            text: shareData.text,
                            url: shareUrl,
                            files: [savedFile.uri]
                        });
                    } catch (err) {
                        console.error('Native community share failed', err);
                        await Share.share({
                            title: shareData.title,
                            text: shareData.text,
                            url: shareUrl
                        });
                    }
                };
                reader.readAsDataURL(blob);
                return;
            }

            if ((navigator as any).share) {
                const webShareData: any = { ...shareData };
                if (blob) {
                    const file = new File([blob], `goodlyfe_community_${Date.now()}.png`, { type: blob.type || 'image/png' });
                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        webShareData.files = [file];
                    }
                }
                await (navigator as any).share(webShareData);
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(shareUrl);
                alert('Link copied to clipboard!');
            } else {
                window.prompt('Share link', shareUrl);
            }
        } catch (err) {
            console.error('Share community action failed', err);
            if (navigator.clipboard) {
                try {
                    await navigator.clipboard.writeText(shareUrl);
                    alert('Link copied to clipboard!');
                } catch (_e) {
                    window.prompt('Share link', shareUrl);
                }
            } else {
                window.prompt('Share link', shareUrl);
            }
        }
    };

    const handleCopyCommunityLink = async () => {
        if (!communityShareUrl) return;
        try {
            await navigator.clipboard.writeText(communityShareUrl);
            alert('Community link copied to clipboard!');
        } catch (err) {
            console.error('Copy link failed', err);
            window.prompt('Copy community link', communityShareUrl);
        }
    };

    const handleDownloadCommunityCard = async () => {
        if (!community) return;
        const blob = await captureCommunityShareBlob();
        if (!blob) {
            alert('Failed to generate community share card.');
            return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `goodlyfe_community_${community.name.replace(/\s+/g, '_').toLowerCase()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleShowCommunityQrCode = () => {
        setShowCommunityQRCode(true);
    };

    const handleReact = async (messageId: string, emoji: string) => {
        if (!community || !currentUserId) return;
        try {
            const updatedMsg = await reactToChannelMessage(community._id, currentChannelId, messageId, currentUserId, emoji);
            setChannelMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: updatedMsg.reactions } : m));
            setActiveMenu(null);

            // Update recent emojis
            updateRecentEmojis(emoji);
        } catch (err) {
            console.error('Failed to react:', err);
        }
    };

    const handleDeleteMessage = async (messageId: string) => {
        if (!community || !currentUserId) return;
        try {
            await deleteChannelMessage(community._id, currentChannelId, messageId, currentUserId);
            setChannelMessages(prev => prev.filter(m => m.id !== messageId));
            setDeletingMessage(null);
            setActiveMenu(null);
        } catch (err) {
            console.error('Failed to delete message:', err);
        }
    };

    const handleReply = (msg: ChannelMessage) => {
        setReplyingTo(msg);
        setActiveMenu(null);
        textareaRef.current?.focus();
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setActiveMenu(null);
    };

    // Message Gestures
    const handleMessageDragStart = (e: React.TouchEvent | React.MouseEvent, msgId: string) => {

        // Prevent menu if clicking a button or clickable element inside
        if ((e.target as HTMLElement).closest('button, a, .cursor-pointer')) {
            return;
        }

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        dragStartXRef.current[msgId] = clientX;

        // Start long press timer
        if (longPressTimeoutRef.current[msgId]) clearTimeout(longPressTimeoutRef.current[msgId]!);
        longPressTimeoutRef.current[msgId] = setTimeout(() => {
            setActiveMenu(msgId);
        }, 600); // 600ms is standard for long press
    };


    const handleMessageDragMove = (e: any, msgId: string) => {
        const startX = dragStartXRef.current[msgId];
        if (startX !== undefined) {
            const currentX = e.touches ? e.touches[0].clientX : e.clientX;
            const diff = currentX - startX;

            // Only allow dragging right for reply gesture
            const limitedDiff = diff > 0 ? Math.min(diff, MAX_DRAG_DISTANCE) : 0;

            setDragOffsets(prev => ({
                ...prev,
                [msgId]: limitedDiff
            }));
        }
    };

    const updateRecentEmojis = (emoji: string) => {
        setRecentEmojis(prev => {
            const next = [emoji, ...prev.filter(e => e !== emoji)].slice(0, 20);
            localStorage.setItem('recent_emojis', JSON.stringify(next));
            return next;
        });
    };

    const handleMessageDragEnd = (msgId: string) => {
        const offset = dragOffsets[msgId] || 0;
        if (offset >= DRAG_THRESHOLD) {
            const msg = channelMessages.find(m => m.id === msgId);
            if (msg) handleReply(msg);
        }

        setDragOffsets(prev => {
            const next = { ...prev };
            delete next[msgId];
            return next;
        });
        delete dragStartXRef.current[msgId];

        if (longPressTimeoutRef.current[msgId]) {
            clearTimeout(longPressTimeoutRef.current[msgId]!);
            longPressTimeoutRef.current[msgId] = null;
        }
    };


    if (isGlobalLoading || isLocalLoading) return <div className="text-center text-gray-400 py-12">Loading group details...</div>;
    if (!community) return <div className="text-center text-red-400 py-12">Community not found.</div>;

    // Filter private community posts (only show to members) and exclude archived posts
    // For public communities, all posts are shown but interaction will be restricted for non-members
    // Also filter by current channel if in a posts channel
    const visibleCommunityPosts = (community.isPrivate && !isMember
        ? []
        : communityPosts).filter(post => {
            if (post.isArchived) return false;
            // Filter by channel if we're in a posts channel
            if (currentChannel?.type === 'posts' && currentChannelId) {
                // Show posts that match the current channel OR posts without channelId (legacy)
                // This ensures backward compatibility while enabling channel separation for new posts
                return !post.channelId || post.channelId === currentChannelId;
            }
            return true;
        });

    const hydratedCommunityPosts = (() => {
        const basePosts = visibleCommunityPosts.map(post => ({
            ...post,
            authorData: usersData[post.author] || { username: post.author, pfp: '', displayName: post.author }
        }));
        const union = [...failedPosts, ...basePosts.filter(p => !failedPosts.some(fp => fp.id === p.id || (fp as any).localId === (p as any).localId))];
        return union.sort((a, b) => {
            if (a.uploadFailed && !b.uploadFailed) return -1;
            if (!a.uploadFailed && b.uploadFailed) return 1;
            return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        });
    })();

    const isInChatOrVoice = currentChannel?.type === 'chat' || currentChannel?.type === 'voice';

    const opacityHex = Math.round((communityOpacity / 100) * 255).toString(16).padStart(2, '0');

    return (
        <div className={isInChatOrVoice ? "fixed inset-0 flex flex-col overflow-hidden" : "relative flex flex-col min-h-full w-full overflow-x-hidden"}>
            {/* Full-screen Background */}
            <div
                className="fixed inset-0 z-0"
                style={(() => {
                    const baseColor = community.bg?.style === 'color' ? community.bg.url : null;
                    if (baseColor) {
                        return { backgroundColor: `${baseColor}${Math.round((communityOpacity / 100) * 255).toString(16).padStart(2, '0')}` };
                    }
                    return { backgroundColor: `rgba(13, 17, 23, ${communityOpacity / 100})` };
                })()}
            />

            <div className={`relative z-10 flex flex-1 w-full ${isInChatOrVoice ? 'overflow-hidden' : 'overflow-x-hidden'}`}>
                {!isMobile && sidebarOpen && (
                    <div
                        className={`bg-gray-950/50 border-r border-white/5 p-4 overflow-y-auto flex flex-col flex-shrink-0 w-48 ${isInChatOrVoice ? '' : 'sticky top-0 h-screen'}`}
                        style={{
                            backgroundColor: community.themeColor ? `${community.themeColor}${opacityHex}` : undefined
                        }}
                    >
                        <div className="mb-6">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Channels</h3>
                            <div className="space-y-1">
                                {channelsToShow.map((channel: Channel, index: number) => (
                                    <div
                                        key={channel.id}
                                        draggable={isAdminOrCreator}
                                        onDragStart={(e) => handleDragStart(e, channel.id)}
                                        onDragOver={(e) => handleDragOver(e, channel.id)}
                                        onDrop={(e) => handleDrop(e, channel.id)}
                                        onDragEnd={handleDragEnd}
                                        className={`relative group ${draggedChannelId === channel.id ? 'opacity-30' : 'opacity-100'} ${dragOverChannelId === channel.id ? 'border-t-2 border-blue-500' : ''} transition-all duration-200`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (channel.type === 'voice') {
                                                    const userData = usersData[currentUserId];
                                                    if (userData?.hasJoinedVoiceBefore) {
                                                        setCurrentChannelId(channel.id);
                                                        onNavigateToCommunity(community._id, channel.id);
                                                        if (onJoinVoice && community) onJoinVoice(community._id, channel.id);
                                                    } else {
                                                        setVoiceChannelToJoin(channel.id);
                                                    }
                                                } else {
                                                    setCurrentChannelId(channel.id);
                                                    onNavigateToCommunity(community._id, channel.id);
                                                }
                                            }}
                                            className={`w-full text-left px-3 py-2 rounded-lg font-medium text-sm transition-all ${currentChannelId === channel.id
                                                ? 'text-white shadow-lg'
                                                : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                                }`}
                                            style={currentChannelId === channel.id ? { backgroundColor: community.themeColor || '#000000' } : {}}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    {channel.type === 'voice' ? (
                                                        <PhoneIcon className="w-4 h-4 flex-shrink-0" />
                                                    ) : (
                                                        <HashtagIcon className="w-4 h-4 flex-shrink-0" />
                                                    )}
                                                    <span className="truncate">{channel.name}</span>
                                                </div>
                                                {isAdminOrCreator && (
                                                    <div className="opacity-0 group-hover:opacity-100 p-0.5 cursor-grab active:cursor-grabbing text-gray-500 hover:text-white transition-opacity flex-shrink-0" title="Drag to reorder">
                                                        <DragHandleIcon className="w-3.5 h-3.5" />
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                        {channel.type === 'voice' && voiceRooms[`${community._id}:${channel.id}`]?.length > 0 && (
                                            <div className="ml-6 mt-1 space-y-1">
                                                {voiceRooms[`${community._id}:${channel.id}`].map(p => (
                                                    <div key={p.socketId} className="flex items-center gap-2 py-0.5">
                                                        <div className={`relative p-0.5 rounded-full transition-all duration-300 ${p.isSpeaking ? 'bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.5)]' : 'bg-transparent'}`}>
                                                            {usersData[p.username]?.pfp ? (
                                                                <img src={usersData[p.username].pfp} alt={p.username} className="w-3 h-3 rounded-full object-cover" />
                                                            ) : (
                                                                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-[6px] font-bold">
                                                                    {p.username[0]?.toUpperCase()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className={`text-[11px] truncate transition-colors ${p.isSpeaking ? 'text-white font-medium' : 'text-gray-400'}`}>{usersData[p.username]?.displayName || p.username}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <hr className="border-white/5 my-4" />
                        <div className="text-xs text-gray-500 space-y-2 flex-1">
                            <button
                                type="button"
                                onClick={() => setShowMembers(true)}
                                disabled={!canViewMembers}
                                className={`flex items-center gap-1 text-left ${canViewMembers ? 'hover:text-white transition-colors cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                                title={!canViewMembers ? (!isMember ? 'Join to see members' : 'Only admins can view members') : undefined}
                            >
                                <strong>Members:</strong> {community.members.length}
                            </button>
                            {community.isPrivate && <p className="text-yellow-600">🔒 Private</p>}
                        </div>
                    </div>
                )}


                {/* Main Content */}
                <div className="flex-1 flex flex-col min-h-0 w-full min-w-0">
                    {/* Header */}
                    <div
                        className={` flex-shrink-0 sticky top-0 z-20 flex flex-col justify-end pb-3 ${isMobile ? 'cursor-pointer select-none' : ''}`}
                        onClick={(e) => {
                            if (isMobile) {
                                if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('a') || (e.target as HTMLElement).closest('input')) {
                                    return;
                                }
                                setIsNavigationModalOpen(true);
                            }
                        }}
                        style={{ backgroundColor: community.themeColor ? `${community.themeColor}${opacityHex}` : undefined, paddingTop: Capacitor.isNativePlatform() ? 'calc(0.25rem + env(safe-area-inset-top, 0px))' : '0.25rem' }}
                    >
                        <div className="w-full px-4">
                            <div className="flex items-center gap-3 sm:gap-4">
                                <div className="flex flex-col gap-2 flex-shrink-0">
                                    {/* Hamburger / menu toggle button */}
                                    <button
                                        type="button"
                                        onClick={() => onBack?.()}
                                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
                                        title="View all communities"
                                        aria-label="Navigate to communities page"
                                    >
                                        <UsersIcon className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="min-w-0 flex items-start sm:items-center gap-3 sm:gap-4 flex-1">
                                    <button
                                        type="button"
                                        onClick={() => isMobile ? setIsNavigationModalOpen(true) : setSidebarOpen(prev => !prev)}
                                        className="flex-shrink-0 group/avatar relative active:scale-95 transition-transform"
                                        title={isMobile ? "Open navigation modal" : "Toggle channels"}
                                    >
                                        {community.avatar ? (
                                            <img src={community.avatar} alt={community.name} className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl object-cover border-2 border-white/10 shadow-lg group-hover/avatar:border-white/30 transition-colors" />
                                        ) : (
                                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl text-raised bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl sm:text-2xl font-bold border-2 border-white/10 shadow-lg group-hover/avatar:border-white/30 transition-colors">
                                                {community.name[0]?.toUpperCase() || '?'}
                                            </div>
                                        )}
                                        {/* Subtle indicator that it's a menu trigger */}
                                        <div className="absolute -bottom-1 -right-1 bg-gray-900 rounded-full p-0.5 border border-white/10 opacity-0 group-hover/avatar:opacity-100 transition-opacity">
                                            <HashtagIcon className="w-3 h-3 text-gray-400" />
                                        </div>
                                    </button>
                                    <div
                                        className={`min-w-0 flex-1 ${isMobile ? 'cursor-pointer' : ''}`}
                                        onClick={() => {
                                            if (isMobile) {
                                                setIsNavigationModalOpen(true);
                                            }
                                        }}
                                    >
                                        <h1 className={`font-bold text-white mb-0 break-words whitespace-normal text-raised leading-tight ${community.name.length > 20 ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl'}`} style={stickerTextStyle(0.35)}>
                                            {community.name.length > 40 ? `${community.name.substring(0, 40)}...` : community.name}
                                        </h1>
                                        <p className="text-sm text-gray-400 flex items-center gap-1" style={stickerTextStyle(0.18)}>
                                            {currentChannel?.type === 'voice' ? (
                                                <PhoneIcon className="w-3 h-3 translate-y-[1px]" />
                                            ) : (
                                                <HashtagIcon className="w-3 h-3 translate-y-[1.5px]" />
                                            )}
                                            {currentChannel?.name || 'posts'} channel
                                        </p>

                                        {/* Active Voice Activity Row */}
                                        {isMember && activeVoiceUsers.length > 0 && (
                                            <div className="flex items-center gap-2 mt-3 overflow-x-auto custom-scrollbar pb-1">
                                                <div className="flex -space-x-2 mr-1">
                                                    {activeVoiceUsers.slice(0, 5).map((p) => (
                                                        <div key={p.socketId} className={`relative p-0.5 rounded-full transition-all duration-300 border-2 border-[#161b22] ${p.isSpeaking ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-white/5'}`}>
                                                            {p.isCameraOn && (p.username === currentUserId ? voiceLocalStream : voiceRemoteStreams[p.socketId]) ? (
                                                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full overflow-hidden">
                                                                    <RemoteStreamView
                                                                        stream={p.username === currentUserId ? voiceLocalStream : voiceRemoteStreams[p.socketId]}
                                                                        isLocal={p.username === currentUserId}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                </div>
                                                            ) : usersData[p.username]?.pfp ? (
                                                                <img src={usersData[p.username].pfp} alt={p.username} className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover" />
                                                            ) : (
                                                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-[8px] sm:text-[10px] font-bold">
                                                                    {p.username[0]?.toUpperCase()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {activeVoiceUsers.length > 5 && (
                                                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gray-800 flex items-center justify-center text-[8px] sm:text-[10px] text-gray-400 font-bold z-10">
                                                            +{activeVoiceUsers.length - 5}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-[11px] sm:text-xs text-gray-400 font-medium whitespace-nowrap">
                                                    {activeVoiceUsers.length} in voice
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                                    {isMember && channelsToShow.some((c: Channel) => c.type === 'voice') && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const voiceChannels = channelsToShow.filter((c: Channel) => c.type === 'voice');
                                                const firstVoice = voiceChannels[0];
                                                if (firstVoice) {
                                                    const userData = usersData[currentUserId];
                                                    if (userData?.hasJoinedVoiceBefore) {
                                                        setCurrentChannelId(firstVoice.id);
                                                        if (onJoinVoice && community) onJoinVoice(community._id, firstVoice.id);
                                                        if (isMobile) setSidebarOpen(false);
                                                    } else {
                                                        setVoiceChannelToJoin(firstVoice.id);
                                                    }
                                                }
                                            }}
                                            className="p-2 rounded-full font-bold bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-all shadow-sm"
                                            title="Join Voice Chat"
                                        >
                                            <PhoneIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                    {isMember && (
                                        <button type="button" onClick={handleOpenSettings} className="p-2 rounded-full font-bold bg-white/10 text-gray-300 hover:bg-white/20 transition-all">
                                            <SettingsIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                        </button>
                                    )}
                                </div>
                            </div>


                            <div className="flex justify-end items-center gap-2 sm:gap-3 flex-shrink-0">
                                {!isMember && (!community.isPrivate || query === community.inviteLink) && (
                                    <button
                                        type="button"
                                        onClick={handleJoinLeave}
                                        className="px-6 py-2 rounded-full font-bold transition-all text-sm text-white hover:brightness-110 shadow-lg"
                                        style={{ backgroundColor: community.themeColor || '#000000' }}
                                    >
                                        Join
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 flex flex-col w-full min-w-0 min-h-0">
                        {currentChannel?.type === 'posts' ? (
                            <div className="flex-1 overflow-y-auto pt-0 pb-32 w-full">
                                {isMember && !isGlobalLoading && !isLocalLoading && (
                                    <div className="w-full mb-4 relative">
                                        {!(community.whoCanPost === 'all' || community.admins?.includes(currentUserId) || community.creator === currentUserId) && (
                                            <div className="bg-gray-800/50 border border-white/10 rounded-2xl p-6 text-center text-gray-400">
                                                Only admins can post in this community.
                                            </div>
                                        )}
                                        {(community.whoCanPost === 'all' || community.admins?.includes(currentUserId) || community.creator === currentUserId) && (
                                            <div className="w-full pt-4 flex flex-col items-center">
                                                <div className="w-full">
                                                    <PostCreator
                                                        onCreatePost={(postData) => {
                                                            console.log('📝 CommunityDetailPage creating post with data:', {
                                                                communityId: postData.communityId,
                                                                channelId: postData.channelId,
                                                                type: postData.type
                                                            });
                                                            return onCreatePost(postData);
                                                        }}
                                                        postBlurOpacity={postBlurOpacity}
                                                        isCommunityPost={true}
                                                        communityId={community._id}
                                                        channelId={currentChannelId}
                                                        onInternalModalStateChange={onInternalModalStateChange}
                                                        closeInternalModalsTrigger={closeInternalModalsTrigger}
                                                        initialSharedContent={initialSharedContent}
                                                        clearSharedContent={clearSharedContent}
                                                        onUploadFailed={onUploadFailed}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="w-full">
                                    {community.isPrivate && !isMember ? (
                                        <div className="flex items-center justify-center py-20 px-4 text-center">
                                            <p className="text-gray-400 text-lg font-medium italic">Join the community to see this channel.</p>
                                        </div>
                                    ) : (
                                        <PostGrid
                                            posts={hydratedCommunityPosts}
                                            isNonMemberOfPublicCommunity={!community.isPrivate && !isMember}
                                            isModalOpen={isModalOpen}
                                            onShare={onShare}
                                            isLoading={isGlobalLoading || isLocalLoading}
                                            onPostClick={onPostClick}
                                            onLike={onLike}
                                            onBump={onBump}
                                            onBookmark={onBookmark}
                                            onEdit={onEdit}
                                            onDelete={onDelete}
                                            onNavigateToProfile={onNavigateToProfile}
                                            onNavigateToHashtag={onNavigateToHashtag}
                                            onNavigateToCommunity={onNavigateToCommunity}
                                            onVouch={onVouch}
                                            onApprovePost={handleApprovePost}
                                            onReportPost={onReportPost}
                                            onUpdatePostcardLayout={onUpdatePostcardLayout}
                                            animatingLikes={animatingLikes}
                                            animatingBumps={animatingBumps}
                                            animatingBookmarks={animatingBookmarks}
                                            galleryAnimation={galleryAnimation}
                                            postBlurOpacity={postBlurOpacity}
                                            preferredColumns={gridColumns}
                                            onColumnsChange={onGridColumnsChange}
                                            preferredScale={preferredScale}
                                            onScaleChange={onScaleChange}
                                            onSwipeTransition={onSwipeTransition}
                                            initialVisibleCount={initialVisibleCount}
                                            onVisibleCountChange={onVisibleCountChange}
                                            onRetryPostUpload={onRetryPostUpload}
                                            isShakingMode={isShakingMode}
                                            postcardGap={postcardGap}
                                            onPostcardGapChange={onPostcardGapChange}
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* Chat/Voice Channel */
                            <>
                                {!isMember ? (
                                    <div className="flex items-center justify-center h-full bg-gray-800/50 rounded-2xl p-12 m-4">
                                        <p className="text-gray-400 text-lg font-medium italic">Join the community to see this channel.</p>
                                    </div>
                                ) : (
                                    <>
                                        {currentChannel?.type === 'voice' ? (
                                            <div id="voice-portal-destination" className="flex-1 flex flex-col min-h-0" />
                                        ) : (
                                            <>
                                                {/* Messages - Scrollable, newest at bottom (flex-col-reverse anchors scroll to bottom) */}
                                                <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col-reverse gap-0.5"
                                                    onTouchStart={(e) => {
                                                        if (e.touches.length === 2) {
                                                            const dist = Math.hypot(
                                                                e.touches[0].pageX - e.touches[1].pageX,
                                                                e.touches[0].pageY - e.touches[1].pageY
                                                            );
                                                            initialPinchDistanceRef.current = dist;
                                                            initialFontSizeRef.current = messageFontSize;
                                                        }
                                                    }}
                                                    onTouchMove={(e) => {
                                                        if (e.touches.length === 2 && initialPinchDistanceRef.current !== null) {
                                                            const currentDist = Math.hypot(
                                                                e.touches[0].pageX - e.touches[1].pageX,
                                                                e.touches[0].pageY - e.touches[1].pageY
                                                            );
                                                            const zoomFactor = currentDist / initialPinchDistanceRef.current;
                                                            const newSize = Math.max(14, Math.min(48, initialFontSizeRef.current * zoomFactor));
                                                            updateMessageFontSize(newSize);
                                                        }
                                                    }}
                                                    onTouchEnd={() => {
                                                        initialPinchDistanceRef.current = null;
                                                    }}>
                                                    {channelMessages.length === 0 ? (
                                                        <p className="text-center text-gray-500 py-8">No messages yet. Start the conversation!</p>
                                                    ) : (
                                                        [...channelMessages].reverse().map((msg, idx, arr) => {
                                                            const prevMsg = arr[idx + 1]; // previous in reversed array = next older message
                                                            const isGrouped = prevMsg && prevMsg.authorId === msg.authorId && (new Date(msg.timestamp).getTime() - new Date(prevMsg.timestamp).getTime() < 300000);
                                                            const isMine = msg.authorId === currentUserId;

                                                            return (
                                                                <ChannelMessageComponent
                                                                    key={msg.id}
                                                                    msg={msg}
                                                                    isGrouped={isGrouped}
                                                                    isMine={isMine}
                                                                    isAdminOrCreator={isAdminOrCreator}
                                                                    currentUserId={currentUserId}
                                                                    usersData={usersData}
                                                                    messageFontSize={messageFontSize}
                                                                    selectedMessageId={selectedMessageId}
                                                                    editingMessageId={editingMessageId}
                                                                    editText={editText}
                                                                    setEditText={setEditText}
                                                                    setEditingMessageId={setEditingMessageId}
                                                                    handleSaveEdit={handleSaveEdit}
                                                                    handleReply={handleReply}
                                                                    handleReact={handleReact}
                                                                    handleMessageDragStart={handleMessageDragStart}
                                                                    handleMessageDragMove={handleMessageDragMove}
                                                                    handleMessageDragEnd={handleMessageDragEnd}
                                                                    dragOffsets={dragOffsets}
                                                                    MAX_DRAG_DISTANCE={MAX_DRAG_DISTANCE}
                                                                    scrollToMessage={scrollToMessage}
                                                                    copyToClipboard={copyToClipboard}
                                                                    setDeletingMessage={setDeletingMessage}
                                                                    setActiveMenu={setActiveMenu}
                                                                    activeMenu={activeMenu}
                                                                    setShowEmojiPickerFor={setShowEmojiPickerFor}
                                                                    onNavigateToProfile={onNavigateToProfile}
                                                                    recentEmojis={recentEmojis}
                                                                    updateRecentEmojis={updateRecentEmojis}
                                                                    onDoubleTap={(mid) => {
                                                                        const now = Date.now();
                                                                        if (now - (lastReactionTimeRef.current[mid] || 0) < 500) return;
                                                                        lastReactionTimeRef.current[mid] = now;
                                                                        const favEmoji = usersData[currentUserId]?.favoriteEmoji || '❤️';
                                                                        handleReact(mid, favEmoji);
                                                                    }}
                                                                    onMediaClick={handleMediaClick}
                                                                />
                                                            );
                                                        })
                                                    )}
                                                </div>

                                                {/* Typing Indicator */}
                                                <div className="px-4 h-6 flex items-center">
                                                    {Object.values(typingUsers).length > 0 && (
                                                        <div className="flex items-center gap-2 animate-in fade-in slide-in-from-bottom-1 duration-300">
                                                            <div className="flex -space-x-2">
                                                                {Object.values(typingUsers).slice(0, 3).map((u, i) => (
                                                                    <img key={u.username} src={u.pfp} className="w-4 h-4 rounded-full" alt="" style={{ zIndex: 3 - i }} />
                                                                ))}
                                                            </div>
                                                            <span className="text-[11px] text-gray-500 font-medium italic">
                                                                {Object.values(typingUsers).length === 1
                                                                    ? `${Object.values(typingUsers)[0].username} is cookin up rn..`
                                                                    : `${Object.values(typingUsers).length} people are cookin up rn..`}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Message Input - Sticky at bottom of content area */}
                                                <div className="w-full flex-shrink-0 bg-transparent p-4 pb-[calc(1rem+var(--safe-area-bottom,0px))] space-y-3 relative z-40 messages-page-no-swipe">
                                                    {sendError && (
                                                        <div className="flex items-center gap-2 bg-red-900/80 text-red-300 text-xs px-3 py-2 rounded-lg backdrop-blur-sm border border-red-500/30">
                                                            <span className="flex-1">⚠️ {sendError}</span>
                                                            <button type="button" onClick={() => setSendError(null)} className="text-red-400 hover:text-white flex-shrink-0">✕</button>
                                                        </div>
                                                    )}

                                                    {/* Reply Preview */}
                                                    {replyingTo && (
                                                        <div className="flex items-center gap-3 bg-white/5 border border-white/10 p-3 rounded-xl animate-in slide-in-from-bottom-2 duration-200">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-0.5">
                                                                    <ReplyIcon className="w-3 h-3 text-blue-400" />
                                                                    <span className="text-[11px] font-bold text-blue-400 uppercase tracking-wider">Replying to {replyingTo.authorId}</span>
                                                                </div>
                                                                <p className="text-xs text-gray-400 truncate italic">"{replyingTo.text}"</p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setReplyingTo(null)}
                                                                className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-gray-500 hover:text-white"
                                                            >
                                                                <CloseIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* Attachments Preview */}
                                                    {currentAttachments.length > 0 && (
                                                        <div className="flex flex-wrap gap-2 pb-1">
                                                            {currentAttachments.map((att, idx) => (
                                                                <div key={idx} className="relative group">
                                                                    {att.type === 'image' ? (
                                                                        <img src={att.url} className="w-16 h-16 rounded-lg object-cover border border-white/10" alt="preview" />
                                                                    ) : (att.type === 'audio' || att.type === 'voice') ? (
                                                                        <div className="w-48 h-16 rounded-lg bg-gray-800 flex items-center justify-center border border-white/10 p-2">
                                                                            <CustomAudioPlayer src={att.url} autoplay={false} />
                                                                        </div>
                                                                    ) : att.type === 'video' ? (
                                                                        <video src={`${att.url}#t=0.001`} className="w-16 h-16 rounded-lg object-cover border border-white/10" playsInline muted preload="metadata" />
                                                                    ) : (
                                                                        <div className="w-16 h-16 rounded-lg bg-gray-800 flex items-center justify-center border border-white/10">
                                                                            <span className="text-[10px] text-gray-400 capitalize">{att.type}</span>
                                                                        </div>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeAttachment(idx)}
                                                                        className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 shadow-lg flex items-center justify-center hover:bg-red-500 transition-colors"
                                                                    >
                                                                        <XIcon className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Mention Dropdown */}
                                                    {showMentionDropdown && suggestedMentions.length > 0 && (
                                                        <div className="absolute bottom-full mb-2 left-4 w-64 bg-gray-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                                                            <ul className="max-h-48 overflow-y-auto">
                                                                {suggestedMentions.map((user, index) => {
                                                                    const isSelected = index === selectedMentionIndex;
                                                                    const userData = usersData[user];
                                                                    return (
                                                                        <li
                                                                            key={user}
                                                                            onClick={() => handleMentionSelect(user)}
                                                                            className={`px-3 py-2 cursor-pointer flex items-center gap-3 transition-colors ${isSelected ? 'bg-blue-600/30' : 'hover:bg-white/5'}`}
                                                                        >
                                                                            <div className="flex-shrink-0">
                                                                                {user === 'chat' ? (
                                                                                    <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center">
                                                                                        <HashtagIcon className="w-4 h-4" />
                                                                                    </div>
                                                                                ) : userData?.pfp ? (
                                                                                    <img src={userData.pfp} alt={user} className="w-8 h-8 rounded-full object-cover" />
                                                                                ) : (
                                                                                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-white">
                                                                                        {user[0]?.toUpperCase()}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex flex-col min-w-0">
                                                                                <span className="text-white text-sm font-medium truncate">
                                                                                    {user === 'chat' ? 'Notify everyone (chat)' : (userData?.displayName || user)}
                                                                                </span>
                                                                                {user !== 'chat' && (
                                                                                    <span className="text-gray-400 text-xs truncate">@{user}</span>
                                                                                )}
                                                                            </div>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    <form onSubmit={handleSendMessage} className="flex gap-2 items-end">
                                                        <div className="w-full flex flex-col gap-2">
                                                            {isRecording && (
                                                                <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl px-4 py-2 border border-blue-500/30 min-h-[48px] flex items-center mb-1">
                                                                    <AudioRecorder
                                                                        ref={audioRecorderRef}
                                                                        onRecordingComplete={(url, blob) => handleVoiceMessageComplete(blob)}
                                                                        onReset={() => {
                                                                            setIsRecording(false);
                                                                            if (audioStream) {
                                                                                audioStream.getTracks().forEach(track => track.stop());
                                                                                setAudioStream(null);
                                                                            }
                                                                        }}
                                                                        autoStart={true}
                                                                        showControls={false}
                                                                        stream={audioStream}
                                                                    />
                                                                </div>
                                                            )}
                                                            <div className="flex-grow flex-shrink flex-1 relative bg-black/40 rounded-full focus-within:border-blue-500/50 transition-all duration-200 flex items-center px-2"
                                                                style={{ backgroundColor: community.themeColor ? `${community.themeColor}${opacityHex}` : undefined }}>

                                                                <div className="flex items-center gap-0.5 py-2">
                                                                    <div className="flex-none relative">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setShowInputMenu(!showInputMenu)}
                                                                            className={`p-2 rounded-xl transition-all ${showInputMenu ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                                            title="More options"
                                                                        >
                                                                            <PlusIcon className={`w-5 h-5 transition-transform duration-200 ${showInputMenu ? 'rotate-45' : ''}`} />
                                                                        </button>
                                                                        {showInputMenu && (
                                                                            <>
                                                                                <div className="fixed inset-0 z-40" onClick={() => setShowInputMenu(false)} />
                                                                                <div className="absolute bottom-full left-0 mb-2 bg-[#1b1b1f] border border-white/10 rounded-full shadow-2xl overflow-hidden flex flex-row items-center p-1 animate-fade-in z-50">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setShowInputMenu(false);
                                                                                            const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                                                                            if (isAndroid) {
                                                                                                setCameraMode('image');
                                                                                                setShowCamera(true);
                                                                                            } else {
                                                                                                setCameraMode('both');
                                                                                                setShowCamera(true);
                                                                                            }
                                                                                        }}
                                                                                        className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                                        title="Camera"
                                                                                    >
                                                                                        <CameraIcon className="w-5 h-5 text-blue-400" />
                                                                                    </button>
                                                                                    {(() => {
                                                                                        const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                                                                        if (isAndroid) {
                                                                                            return (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => {
                                                                                                        setShowInputMenu(false);
                                                                                                        setCameraMode('video');
                                                                                                        setShowCamera(true);
                                                                                                    }}
                                                                                                    className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                                                    title="Record video"
                                                                                                >
                                                                                                    <VideoIcon className="w-5 h-5 text-purple-400" />
                                                                                                </button>
                                                                                            );
                                                                                        }
                                                                                        return null;
                                                                                    })()}
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setShowInputMenu(false);
                                                                                            handleGalleryClick();
                                                                                        }}
                                                                                        className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                                        title="Gallery"
                                                                                    >
                                                                                        <PaperclipIcon className="w-5 h-5 text-green-400" />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setShowInputMenu(false);
                                                                                            setTenorSearchMode('chat');
                                                                                            setShowTenorModal(true);
                                                                                        }}
                                                                                        className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                                        title="GIF"
                                                                                    >
                                                                                        <span className="text-[10px] font-black tracking-widest px-1.5 py-1 rounded bg-pink-500/20 text-pink-400 border border-pink-500/30 leading-none flex items-center justify-center h-5">GIF</span>
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={async () => {
                                                                                            setShowInputMenu(false);
                                                                                            if (!isRecording) {
                                                                                                try {
                                                                                                    await requestMicrophonePermission();
                                                                                                    const stream = await navigator.mediaDevices.getUserMedia({
                                                                                                        audio: VOICE_AUDIO_CONSTRAINTS
                                                                                                    });
                                                                                                    setAudioStream(stream);
                                                                                                    setIsRecording(true);
                                                                                                } catch (err) {
                                                                                                    console.error("Microphone access failed:", err);
                                                                                                }
                                                                                            } else {
                                                                                                audioRecorderRef.current?.stopRecording();
                                                                                                setIsRecording(false);
                                                                                            }
                                                                                        }}
                                                                                        className={`p-2.5 hover:bg-white/5 rounded-full transition-colors ${isRecording ? 'text-red-400' : 'text-white'}`}
                                                                                        title="Voice message"
                                                                                    >
                                                                                        <MicIcon className={`w-5 h-5 ${isRecording ? 'text-red-400 animate-pulse' : 'text-red-400'}`} />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setShowInputMenu(false);
                                                                                            setShowEmojiPickerFor('chat-input');
                                                                                        }}
                                                                                        className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                                        title="Emoji"
                                                                                    >
                                                                                        <EmojiIcon className="w-5 h-5 text-yellow-400" />
                                                                                    </button>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    <input
                                                                        type="file"
                                                                        id="community-file-input"
                                                                        ref={fileInputRef}
                                                                        onChange={handleFileSelect}
                                                                        className="hidden"
                                                                        multiple
                                                                        accept="image/*,video/*,audio/*"
                                                                    />
                                                                </div>

                                                                <textarea
                                                                    ref={textareaRef}
                                                                    className="flex-1 bg-transparent text-white px-3 py-3 focus:outline-none resize-none min-h-[48px] max-h-[150px] transition-all duration-200"
                                                                    rows={1}
                                                                    value={messageInput}
                                                                    onChange={handleInputChange}
                                                                    onKeyDown={handleMessageKeyDown}
                                                                    onPaste={handlePaste}
                                                                    placeholder={currentChannel ? `Message #${currentChannel.name}` : "Message channel"}
                                                                    disabled={isSendingMessage || isUploading}
                                                                />

                                                                <button
                                                                    type="submit"
                                                                    disabled={(!messageInput.trim() && currentAttachments.length === 0) || isSendingMessage || isUploading}
                                                                    className="p-2 hover:bg-white/5 rounded-xl text-blue-500 disabled:opacity-50 transition-all flex-shrink-0 ml-1"
                                                                >
                                                                    {isUploading ? <span className="animate-spin inline-block w-5 h-5 border-2 border-t-transparent border-current rounded-full" /> : <SendIcon className="w-5 h-5" />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </form>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Modals */}

                {
                    showMembers && canViewMembers && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                            <div
                                className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[80vh]"
                                style={{ backgroundColor: community.themeColor ? `${community.themeColor}ee` : undefined }}
                            >
                                <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
                                    <div>
                                        <h2 className="text-xl font-bold text-white">Members</h2>
                                        <p className="text-sm text-gray-500 mt-0.5">{community.members.length} total</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isMember && (
                                            <button
                                                type="button"
                                                onClick={() => { setShowMembers(false); setShowInviteModal(true); }}
                                                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 transition-all text-xs font-black uppercase tracking-widest border border-blue-400/20 shadow-lg"
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                                Add
                                            </button>
                                        )}
                                        <button type="button" onClick={() => setShowMembers(false)} className="text-gray-400 hover:text-white transition-colors p-1">
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="overflow-y-auto flex-1 p-4 space-y-2">
                                    {community.members.map(member => {
                                        const memberData = usersData[member];
                                        const isThisCreator = member === community.creator;
                                        const isThisAdmin = community.admins.includes(member);
                                        const isMe = member === currentUserId;
                                        const isActing = memberActionLoading?.startsWith(member);

                                        return (
                                            <div key={member} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/8 transition-colors">
                                                <button
                                                    type="button"
                                                    className="relative flex-shrink-0 cursor-pointer"
                                                    onClick={() => onNavigateToProfile(member)}
                                                    onMouseDown={() => {
                                                        (window as any).__memberLpFired = false;
                                                        const t = setTimeout(() => { (window as any).__memberLpFired = true; onHoldProfile?.(member); }, 400);
                                                        (window as any).__memberLpTimer = t;
                                                    }}
                                                    onMouseUp={() => { clearTimeout((window as any).__memberLpTimer); }}
                                                    onMouseLeave={() => { clearTimeout((window as any).__memberLpTimer); }}
                                                    onMouseEnter={() => {
                                                        if (window.matchMedia('(pointer: fine)').matches) {
                                                            onHoldProfile?.(member);
                                                        }
                                                    }}
                                                    onTouchStart={() => {
                                                        (window as any).__memberLpFired = false;
                                                        const t = setTimeout(() => { (window as any).__memberLpFired = true; onHoldProfile?.(member); }, 400);
                                                        (window as any).__memberLpTimer = t;
                                                    }}
                                                    onTouchEnd={() => { clearTimeout((window as any).__memberLpTimer); }}
                                                    aria-label={`View ${member}'s profile`}
                                                >
                                                    {memberData?.pfp ? <img src={memberData.pfp} alt={member} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">{member[0]?.toUpperCase()}</div>}
                                                    {isThisCreator && <span className="absolute -bottom-1 -right-1 text-xs bg-yellow-500 text-black rounded-full px-1 leading-4 font-bold">👑</span>}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="flex-1 min-w-0 text-left"
                                                    onClick={() => onNavigateToProfile(member)}
                                                    onMouseDown={() => {
                                                        (window as any).__memberLpFired = false;
                                                        const t = setTimeout(() => { (window as any).__memberLpFired = true; onHoldProfile?.(member); }, 400);
                                                        (window as any).__memberLpTimer = t;
                                                    }}
                                                    onMouseUp={() => { clearTimeout((window as any).__memberLpTimer); }}
                                                    onMouseLeave={() => { clearTimeout((window as any).__memberLpTimer); }}
                                                    onMouseEnter={() => {
                                                        if (window.matchMedia('(pointer: fine)').matches) {
                                                            onHoldProfile?.(member);
                                                        }
                                                    }}
                                                    onTouchStart={() => {
                                                        (window as any).__memberLpFired = false;
                                                        const t = setTimeout(() => { (window as any).__memberLpFired = true; onHoldProfile?.(member); }, 400);
                                                        (window as any).__memberLpTimer = t;
                                                    }}
                                                    onTouchEnd={() => { clearTimeout((window as any).__memberLpTimer); }}
                                                >
                                                    <p className="text-white font-medium text-sm truncate">{memberData?.displayName || member} {isMe && <span className="text-gray-500 text-xs ml-1">(you)</span>}</p>
                                                    <p className="text-gray-500 text-xs truncate">@{member}</p>
                                                </button>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {isThisCreator ? <span className="text-xs bg-yellow-500/15 text-yellow-400 px-2 py-0.5 rounded-full border border-yellow-500/20">Creator</span> : isThisAdmin ? <span className="text-xs bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full border border-blue-500/20">Admin</span> : null}
                                                    {!isMe && !isThisCreator && isAdminOrCreator && (
                                                        <div className="flex gap-1">
                                                            {isThisAdmin ? (isCreator && <button type="button" onClick={() => handleRevokeAdmin(member)} disabled={isActing} className="text-xs px-2 py-1 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-red-500/20 hover:text-red-400 transition-colors disabled:opacity-40">{isActing && memberActionLoading?.endsWith('_revoke') ? '...' : '−Admin'}</button>) : (<button type="button" onClick={() => handleGrantAdmin(member)} disabled={isActing} className="text-xs px-2 py-1 rounded-lg bg-white/5 text-gray-400 hover:bg-blue-500/20 hover:text-blue-400 transition-colors disabled:opacity-40">{isActing && memberActionLoading?.endsWith('_grant') ? '...' : '+Admin'}</button>)}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveMember(member)}
                                                                disabled={isActing || (isThisAdmin && !isCreator)}
                                                                title={isThisAdmin && !isCreator ? 'Only the community creator can remove admins' : undefined}
                                                                className={`text-xs px-2 py-1 rounded-lg transition-colors disabled:opacity-40 ${isThisAdmin && !isCreator ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400'}`}
                                                            >
                                                                {isActing && memberActionLoading?.endsWith('_remove') ? '...' : 'Remove'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )
                }

                {
                    showSettings && isMember && (
                        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 animate-fade-in bg-black/70 backdrop-blur-sm">
                            <div
                                className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col"
                                style={{ backgroundColor: getExactModalBg(settingsForm.themeColor || '#161b22', userOpacity), color: userTextColor }}
                            >
                                <div className="p-8 overflow-y-auto max-h-[90vh]">
                                    <div className="flex items-center justify-between mb-6">
                                        <h2 className="text-2xl font-bold" style={{ color: settingsForm.themeColor || '#161b22', ...stickerTextStyle(0.15) }}>
                                            {isAdminOrCreator ? 'Community Settings' : community.name}
                                        </h2>
                                        <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-white transition-colors">
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                        </button>
                                    </div>

                                    {/* Admin/Creator settings fields */}
                                    {isAdminOrCreator && (
                                        <form onSubmit={handleSaveSettings} className="space-y-4 px-1">
                                            {/* Avatar + Name row */}
                                            <div className="flex items-center gap-4 mb-4">
                                                {/* Avatar Uploader */}
                                                <div className="relative group cursor-pointer flex-shrink-0" style={{ width: '72px', height: '72px' }} onClick={() => pfpFileInputRef.current?.click()}>
                                                    {settingsForm.avatar ? (
                                                        <img src={settingsForm.avatar} alt="Community Avatar" className="w-full h-full rounded-full border-4 border-white/10 object-cover group-hover:opacity-75 transition-opacity" />
                                                    ) : (
                                                        <div className="w-full h-full rounded-full border-4 border-white/10 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold group-hover:opacity-75 transition-opacity">
                                                            {community.name[0]?.toUpperCase() || '?'}
                                                        </div>
                                                    )}
                                                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <CameraIcon className="w-5 h-5" />
                                                    </div>
                                                    <input type="file" ref={pfpFileInputRef} onChange={handlePfpFileChange} accept="image/*" className="hidden" />
                                                </div>

                                                {/* Community Name (creator only) */}
                                                {isCreator ? (
                                                    <div className="flex-1 min-w-0">
                                                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-1" style={stickerTextStyle(0.18)}>Community Name</label>
                                                        <input value={settingsForm.name} onChange={e => setSettingsForm(prev => ({ ...prev, name: e.target.value }))} onBlur={() => updateCommunityField({ name: settingsForm.name })} required className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl px-4 py-2 text-white focus:outline-none" placeholder="Community name" />
                                                    </div>
                                                ) : (
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-base font-bold text-white truncate">{community.name}</p>
                                                    </div>
                                                )}
                                            </div>

                                            <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-1" style={stickerTextStyle(0.18)}>Bio / Description</label><textarea value={settingsForm.bio} onChange={e => setSettingsForm(prev => ({ ...prev, bio: e.target.value }))} onBlur={() => updateCommunityField({ bio: settingsForm.bio })} className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl px-4 py-2 text-white focus:outline-none h-24 resize-none" placeholder="What is this group about?" /></div>


                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black uppercase tracking-tight text-gray-400" style={stickerTextStyle(0.18)}>Community Theme Color</label>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: settingsForm.themeColor }}>
                                                        <input
                                                            type="color"
                                                            value={settingsForm.themeColor}
                                                            onChange={(e) => updateCommunityField({ themeColor: e.target.value })}
                                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                        />
                                                    </div>
                                                    <input
                                                        type="text"
                                                    value={settingsForm.themeColor}
                                                    onChange={(e) => setSettingsForm(prev => ({ ...prev, themeColor: e.target.value }))}
                                                    onBlur={() => updateCommunityField({ themeColor: settingsForm.themeColor })}
                                                    className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-4 text-xs font-mono text-white"
                                                    placeholder="#161b22"
                                                />
                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            copyToClipboard(settingsForm.themeColor || '#161b22');
                                                        }}
                                                        className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                                                        title="Copy Hex"
                                                    >
                                                        <CopyIcon className="w-4 h-4 opacity-60 text-white" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                            try {
                                                                const text = await readFromClipboard();
                                                                if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) {
                                                                    updateCommunityField({ themeColor: text });
                                                                }
                                                            } catch (err) { }
                                                        }}
                                                        className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                                                        title="Paste Hex"
                                                    >
                                                        <PasteIcon className="w-4 h-4 opacity-60 text-white" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Community Transparency */}
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <label className="text-[10px] font-black uppercase tracking-tight text-gray-400" style={stickerTextStyle(0.18)}>Theme Transparency</label>
                                                <span className="text-xs font-black text-white/60">{communityOpacity}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={communityOpacity}
                                                onPointerDown={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                    const val = Number(e.target.value);
                                                    setCommunityOpacity(val);
                                                    localStorage.setItem(communityOpacityKey, String(val));
                                                }}
                                                className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                                style={{
                                                    background: `linear-gradient(to right, ${settingsForm.themeColor || '#3b82f6'} ${communityOpacity}%, rgba(255,255,255,0.1) ${communityOpacity}%)`
                                                }}
                                            />
                                            <p className="text-[10px] text-gray-500" style={stickerTextStyle(0.18)}>Only applies on your device.</p>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="text-[10px] font-black uppercase tracking-tight text-gray-400" style={stickerTextStyle(0.18)}>Community Background</label>
                                                <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                                    {(['image', 'color'] as const).map(mode => (
                                                        <button
                                                            key={mode}
                                                            type="button"
                                                            onClick={() => {
                                                                if (mode === 'color') {
                                                                    updateCommunityField({ bgMode: 'color', bg: { url: localBgColor, style: 'color' } });
                                                                } else {
                                                                    updateCommunityField({ bgMode: 'image', bg: { url: activeSlideshow.attachments[0]?.url || '', style: 'cover' } });
                                                                }
                                                            }}
                                                            className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${settingsForm.bgMode === mode ? 'bg-white text-black shadow-lg' : 'text-white hover:text-white'}`}
                                                        >
                                                            {mode}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {settingsForm.bgMode === 'image' ? (
                                                <div className="space-y-4 animate-fade-in">
                                                    <SlideshowBuilder
                                                        slideshow={activeSlideshow}
                                                        onChangeSlideshow={handleCommunitySlideshowChange}
                                                        nativeInputRef={bgFileInputRef as React.RefObject<HTMLInputElement>}
                                                        onNativeUpload={handleCommunityBgUpload}
                                                        onOpenTenor={() => { setTenorSearchMode('background'); setShowTenorModal(true); }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-4 animate-fade-in">
                                                    <div className="w-full h-32 rounded-2xl border border-white/10 flex items-center justify-center relative transition-all overflow-hidden" style={{ backgroundColor: localBgColor }}>
                                                        <input
                                                            type="color"
                                                            value={localBgColor}
                                                            onChange={(e) => {
                                                                const c = e.target.value;
                                                                setLocalBgColor(c);
                                                                updateCommunityField({ bg: { url: c, style: 'color' } });
                                                            }}
                                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                        />
                                                        <div className="p-3 bg-black/40 backdrop-blur-md rounded-xl border border-white/10 text-[10px] font-black uppercase tracking-widest pointer-events-none text-white">
                                                            Change Color
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="space-y-4 pt-1 px-2">
                                                {settingsForm.bgMode === 'image' && (
                                                    <div className="space-y-4 animate-fade-in">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[9px] font-bold uppercase" style={stickerTextStyle(0.18)}>Style</span>
                                                            <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                                                {(['cover', 'tile'] as const).map(style => (
                                                                    <button
                                                                        key={style}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            updateCommunityField({ bg: { ...settingsForm.bg, style } });
                                                                        }}
                                                                        className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${(settingsForm.bg.style === 'color' ? 'cover' : settingsForm.bg.style) === style ? 'bg-white/20 text-white' : 'text-white hover:text-white'}`}
                                                                    >
                                                                        {style === 'cover' ? 'Fill' : 'Tile'}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {(settingsForm.bg.style === 'tile') && (
                                                            <div className="space-y-4 animate-fade-in">
                                                                <div className="flex justify-between items-center">
                                                                    <label className="text-[9px] font-bold uppercase text-gray-400">Tile Resolution</label>
                                                                    <div className="flex items-center gap-1">
                                                                        <input
                                                                            type="number"
                                                                            min="25"
                                                                            max="200"
                                                                            value={settingsForm.bgResolution}
                                                                            onChange={(e) => {
                                                                                const val = Number(e.target.value);
                                                                                if (!isNaN(val) && val >= 25 && val <= 200) {
                                                                                    updateCommunityField({ bgResolution: val });
                                                                                    if (onPreviewBgResolution) onPreviewBgResolution(val);
                                                                                }
                                                                            }}
                                                                            onPointerDown={(e) => e.stopPropagation()}
                                                                            className="w-12 bg-black/50 border border-white/10 rounded-md text-xs font-black text-blue-400 text-center py-0.5"
                                                                        />
                                                                        <span className="text-xs font-black text-blue-400">%</span>
                                                                    </div>
                                                                </div>
                                                                <input
                                                                    type="range"
                                                                    min="25"
                                                                    max="200"
                                                                    value={settingsForm.bgResolution}
                                                                    onPointerDown={(e) => handleSliderPointerDown(e, settingsForm.bgResolution, 25, 200)}
                                                                    onChange={(e) => {
                                                                        const res = Number(e.target.value);
                                                                        updateCommunityField({ bgResolution: res });
                                                                        if (onPreviewBgResolution) onPreviewBgResolution(res);
                                                                    }}
                                                                    className="w-full h-1.5 bg-black/40 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                               <div className="space-y-4 py-2 border-t border-white/10 pt-4">
                                                {/* Private Community Switch */}
                                                <div className="flex items-center justify-between p-3 bg-black/20 border border-white/10 rounded-2xl">
                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-white font-bold" style={stickerTextStyle(0.18)}>Private Community</span>
                                                        <span className="text-[10px] text-gray-400">Invite only access</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => updateCommunityField({ isPrivate: !settingsForm.isPrivate })}
                                                        className={`w-11 h-6 rounded-full p-1 transition-colors duration-200 focus:outline-none ${settingsForm.isPrivate ? 'bg-green-500' : 'bg-white/10'}`}
                                                    >
                                                        <div
                                                            className={`w-4 h-4 rounded-full bg-white transition-transform duration-200 ${settingsForm.isPrivate ? 'translate-x-5' : 'translate-x-0'}`}
                                                        />
                                                    </button>
                                                </div>

                                                {/* Post Moderation Approval Switch */}
                                                <div className="flex items-center justify-between p-3 bg-black/20 border border-white/10 rounded-2xl">
                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-white font-bold" style={stickerTextStyle(0.18)}>Require Admin Approval for Posts</span>
                                                        <span className="text-[10px] text-gray-400">Posts by non-admins must be approved before members can see them</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => updateCommunityField({ requireApprovalForPosts: !settingsForm.requireApprovalForPosts })}
                                                        className={`w-11 h-6 rounded-full p-1 transition-colors duration-200 focus:outline-none ${settingsForm.requireApprovalForPosts ? 'bg-green-500' : 'bg-white/10'}`}
                                                    >
                                                        <div
                                                            className={`w-4 h-4 rounded-full bg-white transition-transform duration-200 ${settingsForm.requireApprovalForPosts ? 'translate-x-5' : 'translate-x-0'}`}
                                                        />
                                                    </button>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    {/* Who Can Post Switch */}
                                                    <div className="flex flex-col gap-2 p-3 bg-black/20 border border-white/10 rounded-2xl">
                                                        <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400" style={stickerTextStyle(0.18)}>Who can post?</label>
                                                        <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                                            {(['all', 'admins'] as const).map(option => (
                                                                <button
                                                                    key={option}
                                                                    type="button"
                                                                    onClick={() => updateCommunityField({ whoCanPost: option })}
                                                                    className={`flex-1 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${settingsForm.whoCanPost === option ? 'bg-white text-black shadow-lg' : 'text-white/60 hover:text-white'}`}
                                                                >
                                                                    {option === 'all' ? 'All' : 'Admins'}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Who Can View Members Switch */}
                                                    <div className="flex flex-col gap-2 p-3 bg-black/20 border border-white/10 rounded-2xl">
                                                        <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400" style={stickerTextStyle(0.18)}>Who can view members?</label>
                                                        <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                                            {(['all', 'admins'] as const).map(option => (
                                                                <button
                                                                    key={option}
                                                                    type="button"
                                                                    onClick={() => updateCommunityField({ whoCanViewMembers: option })}
                                                                    className={`flex-1 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${settingsForm.whoCanViewMembers === option ? 'bg-white text-black shadow-lg' : 'text-white/60 hover:text-white'}`}
                                                                >
                                                                    {option === 'all' ? 'All' : 'Admins'}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="border-t border-white/10 pt-4 mt-2">
                                                <div className="flex items-center justify-between mb-3">
                                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-400" style={stickerTextStyle(0.18)}>Manage Channels</label>
                                                    <div className="flex gap-2">
                                                        <button type="button" onClick={() => handleAddChannel('chat')} className="text-[10px] px-2.5 py-1 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 transition-colors">+Chat</button>
                                                        <button type="button" onClick={() => handleAddChannel('voice')} className="text-[10px] px-2.5 py-1 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 transition-colors">+Voice</button>
                                                        <button type="button" onClick={() => handleAddChannel('posts')} className="text-[10px] px-2.5 py-1 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 transition-colors">+Posts</button>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    {settingsForm.channels.map((ch, idx) => (
                                                        <div
                                                            key={ch.id}
                                                            draggable
                                                            onDragStart={(e) => handleDragStart(e, ch.id)}
                                                            onDragOver={(e) => handleDragOver(e, ch.id)}
                                                            onDrop={(e) => handleDrop(e, ch.id)}
                                                            onDragEnd={handleDragEnd}
                                                            className={`flex items-center gap-2 bg-black/20 p-2 rounded-2xl border transition-all cursor-move ${draggedChannelId === ch.id ? 'opacity-40 scale-95 border-blue-500/50' : dragOverChannelId === ch.id ? 'border-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.3)] bg-blue-500/5' : 'border-white/5 hover:border-white/10 hover:bg-black/30'}`}
                                                        >
                                                            <div className="text-gray-400 flex items-center justify-center w-8">
                                                                {ch.type === 'voice' ? (
                                                                    <PhoneIcon className="w-4 h-4" />
                                                                ) : (
                                                                    <HashtagIcon className="w-4 h-4" />
                                                                )}
                                                            </div>
                                                            <input
                                                                value={ch.name}
                                                                onChange={(e) => handleRenameChannel(ch.id, e.target.value)}
                                                                onBlur={() => updateCommunityField({ channels: settingsForm.channels })}
                                                                className="flex-1 bg-transparent border-none text-sm text-white focus:ring-0 p-0 focus:outline-none"
                                                                placeholder="channel-name"
                                                                onClick={(e) => e.stopPropagation()} // Prevent drag when clicking input
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); handleRemoveChannel(ch.id); }}
                                                                className="text-gray-400 hover:text-red-400 transition-colors p-1"
                                                                title="Delete channel"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="border-t border-white/10 pt-4 mt-4">
                                                <label className="text-[10px] font-black uppercase tracking-tight text-gray-400 mb-3 block" style={stickerTextStyle(0.18)}>Invite & Share</label>
                                                <div className="bg-black/20 border border-white/10 rounded-2xl p-4 space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-400" style={stickerTextStyle(0.18)}>Invite Link</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const baseOrigin = typeof window !== 'undefined' && ((window as any).Capacitor !== 'undefined' || window.location.hostname === 'localhost') ? 'https://goodlyfe.net' : (typeof window !== 'undefined' ? window.location.origin : '');
                                                                const url = community.isPrivate
                                                                    ? `${baseOrigin}/community/${encodeURIComponent(community.inviteLink || '')}`
                                                                    : `${baseOrigin}/community/${encodeURIComponent(community.name)}`;
                                                                navigator.clipboard.writeText(url);
                                                                alert('Invite link copied!');
                                                            }}
                                                            className="text-[10px] font-bold hover:brightness-110 transition-colors uppercase tracking-widest"
                                                            style={{ color: settingsForm.themeColor || '#161b22' }}
                                                        >
                                                            Copy Link
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleShareCommunity}
                                                        className="w-full py-2.5 rounded-xl bg-white/5 text-white text-xs font-bold hover:bg-white/10 transition-all border border-white/10"
                                                    >
                                                        Share Community
                                                    </button>
                                                </div>
                                            </div>

                                            {renderChannelNotifSettings()}

                                            <div className="flex gap-4 mt-6">
                                                <button type="button" onClick={() => setShowSettings(false)} className="w-full py-3 rounded-2xl bg-white/5 text-white hover:bg-white/10 transition-colors font-bold">Close</button>
                                            </div>
                                        </form>
                                    )}

                                    {!isAdminOrCreator && (
                                        <div className="space-y-6 px-1">
                                            <div className="flex flex-col items-center">
                                                <div className="w-24 h-24 rounded-full border-4 border-white/10 shadow-xl overflow-hidden mb-4">
                                                    {community.avatar ? (
                                                        <img src={community.avatar} alt={community.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold">
                                                            {community.name[0]?.toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                                <h3 className="text-xl font-bold mb-1" style={{ color: settingsForm.themeColor || '#161b22', ...stickerTextStyle(0.15) }}>{community.name}</h3>
                                                {community.isPrivate && <span className="text-[10px] font-black text-yellow-500 uppercase tracking-[0.2em] bg-yellow-500/10 px-3 py-1 rounded-full border border-yellow-500/20">Private Community</span>}
                                            </div>

                                            {community.bio && (
                                                <div className="bg-black/20 border border-white/10 rounded-2xl p-5 shadow-inner">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-3 block" style={stickerTextStyle(0.18)}>About</label>
                                                    <div className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
                                                        <FormattedText
                                                            text={community.bio}
                                                            onNavigateToProfile={onNavigateToProfile}
                                                            onNavigateToHashtag={onNavigateToHashtag}
                                                            textSize="sm"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black uppercase tracking-tight text-gray-400 mb-1 block" style={stickerTextStyle(0.18)}>Invite & Share</label>
                                                <div className="bg-black/20 border border-white/10 rounded-2xl p-4 space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-400" style={stickerTextStyle(0.18)}>Invite Link</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const baseOrigin = typeof window !== 'undefined' && ((window as any).Capacitor !== 'undefined' || window.location.hostname === 'localhost') ? 'https://goodlyfe.net' : (typeof window !== 'undefined' ? window.location.origin : '');
                                                                const url = community.isPrivate
                                                                    ? `${baseOrigin}/community/${encodeURIComponent(community.inviteLink || '')}`
                                                                    : `${baseOrigin}/community/${encodeURIComponent(community.name)}`;
                                                                navigator.clipboard.writeText(url);
                                                                alert('Invite link copied!');
                                                            }}
                                                            className="text-[10px] font-bold hover:brightness-110 transition-colors uppercase tracking-widest"
                                                            style={{ color: settingsForm.themeColor || '#161b22' }}
                                                        >
                                                            Copy Link
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleShareCommunity}
                                                        className="w-full py-2.5 rounded-xl bg-white/5 text-white text-xs font-bold hover:bg-white/10 transition-all border border-white/10"
                                                    >
                                                        Share Community
                                                    </button>
                                                </div>
                                            </div>

                                            {renderChannelNotifSettings()}

                                            <button
                                                type="button"
                                                onClick={() => setShowSettings(false)}
                                                className="w-full py-3 rounded-2xl bg-white/5 text-white hover:bg-white/10 transition-colors font-bold mt-4"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    )}

                                    {/* Leave Group — only for non-creator members */}
                                    {!isCreator && (
                                        <div className={isAdminOrCreator ? 'mt-6 pt-6 border-t border-white/10' : ''}>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    if (window.confirm(`Leave "${community.name}"? You can rejoin later if it's public.`)) {
                                                        setShowSettings(false);
                                                        await handleJoinLeave();
                                                    }
                                                }}
                                                className="w-full py-3 rounded-2xl bg-red-500/10 text-red-400 hover:bg-red-500/20 font-semibold transition-colors border border-red-500/20"
                                            >
                                                Leave Group
                                            </button>
                                        </div>
                                    )}

                                    {/* Close button for non-admin members with no leave option (creators) */}
                                    {isCreator && !isAdminOrCreator && (
                                        <button type="button" onClick={() => setShowSettings(false)} className="w-full mt-6 py-3 rounded-2xl bg-white/5 text-white hover:bg-white/10 transition-colors font-bold">
                                            Close
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                {showShareCommunityModal && community && (
                    <div className="fixed inset-0 z-[205] flex items-center justify-center p-4 bg-black/80 animate-fade-in" onClick={() => setShowShareCommunityModal(false)}>
                        <div className="relative w-full max-w-xl rounded-[2.5rem] overflow-hidden bg-[#0f172a] shadow-2xl border border-white/10" onClick={e => e.stopPropagation()}>
                            <div className="p-6 border-b border-white/10">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <h3 className="text-2xl font-bold text-white">Share Community</h3>
                                        <p className="text-sm text-gray-400">Generate a share card and send it to friends.</p>
                                    </div>
                                    <button onClick={() => setShowShareCommunityModal(false)} className="p-2 rounded-full bg-white/5 text-gray-300 hover:bg-white/10 transition-colors">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="rounded-3xl overflow-hidden bg-black/40 border border-white/10" ref={communityShareRef}>
                                    <div className="p-4 bg-[#111827]">
                                        <PostScreenshot post={communitySharePost as any} authorData={communityAuthorData as any} postBlurOpacity={90} imageBgMode="border" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={handleShowCommunityQrCode}
                                        className="w-full py-3 rounded-2xl bg-white/5 text-white font-semibold hover:bg-white/10 transition-all border border-white/10"
                                    >
                                        QR Code
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDownloadCommunityCard}
                                        disabled={shareCommunityGenerating}
                                        className="w-full py-3 rounded-2xl bg-white/5 text-white font-semibold hover:bg-white/10 transition-all border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {shareCommunityGenerating ? 'Generating...' : 'Download'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleShareCommunityAction}
                                        className="w-full py-3 rounded-2xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all"
                                    >
                                        Share
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCopyCommunityLink}
                                        className="w-full py-3 rounded-2xl bg-white/5 text-white font-semibold hover:bg-white/10 transition-all border border-white/10"
                                    >
                                        Copy Link
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showCommunityQRCode && (
                    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-black/90 animate-fade-in" onClick={() => setShowCommunityQRCode(false)}>
                        <div className="bg-white rounded-3xl p-6 max-w-sm w-full flex flex-col items-center gap-5 shadow-2xl" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-between w-full">
                                <h3 className="text-2xl font-bold text-gray-900">QR Code</h3>
                                <button onClick={() => setShowCommunityQRCode(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="bg-white p-4 rounded-2xl border-2 border-gray-200">
                                <QRCodeSVG value={communityShareUrl} size={200} level="H" includeMargin={true} />
                            </div>
                            <p className="text-gray-600 text-sm text-center">Scan to join {community.name}</p>
                            <button
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(communityShareUrl);
                                        alert('Link copied!');
                                    } catch (e) {
                                        alert('Failed to copy URL');
                                    }
                                }}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-2xl transition-all"
                            >
                                Copy URL
                            </button>
                        </div>
                    </div>
                )}

                <GifPickerModal
                    isOpen={showTenorModal}
                    onClose={() => setShowTenorModal(false)}
                    onSelectGif={(url, item) => {
                        if (tenorSearchMode === 'chat') {
                            handleAttachTenorGif(url);
                        } else {
                            const currentSlideshow = settingsForm.bgSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
                            const newAttachments = [...(currentSlideshow.attachments || []), {
                                id: `tenor-${Date.now()}-${item?.id || 'gif'}`,
                                url: url,
                                type: 'image',
                                name: item?.name || 'GIF'
                            }];
                            updateCommunityField({
                                bgSlideshow: { ...currentSlideshow, attachments: newAttachments },
                                bg: {
                                    url: newAttachments[0]?.url || '',
                                    style: settingsForm.bg.style || 'cover'
                                }
                            });
                            setShowTenorModal(false);
                        }
                    }}
                    title={tenorSearchMode === 'background' ? "Search Background GIFs" : "Search GIFs"}
                    zIndexClass="z-[150]"
                />

                {isCropping && cropImage && (
                    <PfpCropperModal
                        image={cropImage}
                        onClose={() => { setIsCropping(false); setCropImage(null); }}
                        onCropComplete={handleCropComplete}
                    />
                )}

                {deletingMessage && (
                    <DeleteMessageModal
                        isOpen={!!deletingMessage}
                        onClose={() => setDeletingMessage(null)}
                        onConfirm={() => {
                            if (deletingMessage) {
                                handleDeleteMessage(deletingMessage.id);
                                setDeletingMessage(null);
                            }
                        }}
                        messageText={deletingMessage?.text}
                    />
                )}

                {showEmojiPickerFor && (
                    <EmojiPickerModal
                        isOpen={!!showEmojiPickerFor}
                        onClose={() => setShowEmojiPickerFor(null)}
                        onEmojiSelect={(emoji) => {
                            updateRecentEmojis(emoji);
                            if (showEmojiPickerFor === 'chat-input') {
                                setMessageInput(prev => prev + emoji);
                            } else {
                                handleReact(showEmojiPickerFor, emoji);
                            }
                            setShowEmojiPickerFor(null);
                        }}
                    />
                )}

                {showInviteModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <div
                            className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[80vh]"
                            style={{ backgroundColor: community?.themeColor ? `${community.themeColor}ee` : undefined }}
                        >
                            <div className="p-6 border-b border-white/10 flex-shrink-0">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h2 className="text-xl font-bold text-raised">Add Members</h2>
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">Add friends and followers to community</p>
                                    </div>
                                    <button type="button" onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-white transition-colors p-1">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                    </button>
                                </div>

                                <div className="relative">
                                    <input
                                        type="text"
                                        value={inviteSearchTerm}
                                        onChange={(e) => setInviteSearchTerm(e.target.value)}
                                        placeholder="Search friends & followers..."
                                        className="w-full bg-black/10 border-none rounded-full px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
                                    />
                                </div>
                            </div>

                            <div className="overflow-y-auto flex-1 p-4 space-y-1">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2 px-1">Top Suggestions</p>
                                {filteredWeightedFollowers.length > 0 ? (
                                    filteredWeightedFollowers.map(followerId => {
                                        const followerData = usersData[followerId];
                                        const isInviting = invitingUserIds.has(followerId);

                                        return (
                                            <div key={followerId} className="flex items-center gap-3 py-2 hover:bg-black/20 rounded-full px-2 transition-all group">
                                                <div className="flex-shrink-0">
                                                    {followerData?.pfp ? (
                                                        <img src={followerData.pfp} alt={followerId} className="w-9 h-9 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                                                            {followerId ? followerId.charAt(0).toUpperCase() : '?'}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold truncate text-white">{followerData?.displayName || followerId}</p>
                                                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest truncate">@{followerId}</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleInviteFollower(followerId)}
                                                    disabled={isInviting}
                                                    className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${isInviting ? 'bg-gray-700 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95 shadow-lg shadow-blue-900/20'}`}
                                                >
                                                    {isInviting ? 'Adding...' : 'Add'}
                                                </button>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-center opacity-50">
                                        <div className="w-16 h-16 rounded-3xl bg-white/5 flex items-center justify-center mb-4 border border-white/10 rotate-12 group-hover:rotate-0 transition-transform">
                                            <PlusIcon className="w-8 h-8 text-gray-500" />
                                        </div>
                                        <p className="text-sm font-bold text-white">No followers to invite</p>
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1 max-w-[200px]">
                                            {inviteSearchTerm ? "No followers match your search." : "All your followers are already members."}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="p-4 border-t border-white/10 flex justify-end">
                                <button
                                    onClick={() => { setShowInviteModal(false); setShowMembers(true); }}
                                    className="px-6 py-2 text-gray-400 hover:text-white transition-colors text-xs font-black uppercase tracking-widest"
                                >
                                    Back
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Voice Join Confirmation Modal */}
            {voiceChannelToJoin && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] animate-fade-in" onClick={() => setVoiceChannelToJoin(null)}>
                    <div className="rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center backdrop-blur-md bg-gray-900 border border-white/10" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center mb-4">
                            <PhoneIcon className="w-16 h-16 text-blue-500" />
                        </div>
                        <h2 className="text-2xl font-bold mb-4 text-white">Join Voice Channel?</h2>
                        <p className="text-gray-300 mb-6 font-medium">
                            Join {community?.channels?.find(c => c.id === voiceChannelToJoin)?.name || 'voice'}?
                        </p>
                        <p className="text-xs text-gray-500 mb-6 bg-white/5 p-3 rounded-lg leading-relaxed">
                            By joining, you agree to our safety guidelines. Your microphone will be active. Please be respectful to other members.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => {
                                    if (voiceChannelToJoin) {
                                        setCurrentChannelId(voiceChannelToJoin);
                                        if (onJoinVoice && community) onJoinVoice(community._id, voiceChannelToJoin);
                                        if (isMobile) setIsNavigationModalOpen(false);

                                        // Persist that user has joined voice before
                                        const userData = usersData[currentUserId];
                                        if (!userData?.hasJoinedVoiceBefore) {
                                            updateUserData((prev: any) => ({
                                                ...prev,
                                                [currentUserId]: { ...prev[currentUserId], hasJoinedVoiceBefore: true }
                                            }));
                                            if (!isDemo) {
                                                updateUserOnServer(currentUserId, { hasJoinedVoiceBefore: true }).catch(console.error);
                                            }
                                        }
                                    }
                                    setVoiceChannelToJoin(null);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full transition-all active:scale-95 shadow-lg shadow-blue-500/20"
                            >
                                Accept & Join
                            </button>
                            <button
                                onClick={() => setVoiceChannelToJoin(null)}
                                className="text-gray-400 hover:text-white font-bold py-3 px-6 rounded-full transition-colors"
                            >
                                Decline
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Community Navigation Modal (Mobile Only) */}
            {isMobile && isNavigationModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-[150] animate-fade-in bg-black/60" onClick={() => setIsNavigationModalOpen(false)}>
                    <div
                        className="rounded-3xl shadow-2xl flex flex-col w-full max-w-sm mx-4 overflow-hidden border border-white/10"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: community.themeColor ? `${community.themeColor}${opacityHex}` : undefined,
                            color: '#ffffff'
                        }}
                    >
                        {/* Modal Header */}
                        <div className="flex-shrink-0 p-5 border-b border-white/10 bg-black/25 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {community.avatar ? (
                                    <img src={community.avatar} alt={community.name} className="w-10 h-10 rounded-xl object-cover border border-white/20" />
                                ) : (
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold border border-white/20">
                                        {community.name[0]?.toUpperCase() || '?'}
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <h2 className="text-lg font-black uppercase italic tracking-tighter truncate max-w-[180px]">
                                        {community.name}
                                    </h2>
                                    <p className="text-[10px] text-white/60 tracking-wider font-semibold uppercase">Channels</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsNavigationModalOpen(false)}
                                className="p-2 hover:bg-white/10 active:scale-95 rounded-full transition-all text-white/70 hover:text-white"
                            >
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Content - Channels List */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[50vh] no-scrollbar">
                            {channelsToShow.map((channel: Channel, index: number) => {
                                const isCurrent = currentChannelId === channel.id;
                                return (
                                    <div
                                        key={channel.id}
                                        draggable={isAdminOrCreator}
                                        onDragStart={(e) => handleDragStart(e, channel.id)}
                                        onDragOver={(e) => handleDragOver(e, channel.id)}
                                        onDrop={(e) => handleDrop(e, channel.id)}
                                        onDragEnd={handleDragEnd}
                                        className={`relative group rounded-2xl transition-all duration-200 ${draggedChannelId === channel.id ? 'opacity-30' : 'opacity-100'} ${dragOverChannelId === channel.id ? 'border-t-2 border-blue-500' : ''}`}
                                    >
                                        <div
                                            className={`w-full flex items-center justify-between p-3 rounded-2xl transition-all cursor-pointer ${isCurrent
                                                    ? 'bg-white text-black shadow-lg scale-[1.02]'
                                                    : 'bg-white/5 text-white/80 hover:bg-white/10 hover:text-white hover:scale-[1.01]'
                                                }`}
                                            onClick={() => {
                                                if (channel.type === 'voice') {
                                                    const userData = usersData[currentUserId];
                                                    if (userData?.hasJoinedVoiceBefore) {
                                                        setCurrentChannelId(channel.id);
                                                        onNavigateToCommunity(community._id, channel.id);
                                                        if (onJoinVoice && community) onJoinVoice(community._id, channel.id);
                                                        setIsNavigationModalOpen(false);
                                                    } else {
                                                        setVoiceChannelToJoin(channel.id);
                                                    }
                                                } else {
                                                    setCurrentChannelId(channel.id);
                                                    onNavigateToCommunity(community._id, channel.id);
                                                    setIsNavigationModalOpen(false);
                                                }
                                            }}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                {channel.type === 'voice' ? (
                                                    <PhoneIcon className="w-5 h-5 flex-shrink-0" />
                                                ) : (
                                                    <HashtagIcon className="w-5 h-5 flex-shrink-0" />
                                                )}
                                                <span className="font-bold text-sm truncate">{channel.name}</span>
                                            </div>

                                            {/* Drag Handle if Admin/Creator */}
                                            {isAdminOrCreator && (
                                                <div className="p-1 cursor-grab active:cursor-grabbing text-white/40 hover:text-white/80 transition-colors" title="Drag to reorder">
                                                    <DragHandleIcon className="w-4 h-4" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Nested Voice Users */}
                                        {channel.type === 'voice' && voiceRooms[`${community._id}:${channel.id}`]?.length > 0 && (
                                            <div className="ml-5 mt-2 mb-3 pl-3 border-l-2 border-white/10 space-y-1.5 animate-slide-down">
                                                {voiceRooms[`${community._id}:${channel.id}`].map(p => (
                                                    <div key={p.socketId} className="flex items-center gap-2.5 py-0.5">
                                                        <div className={`relative p-0.5 rounded-full transition-all duration-300 ${p.isSpeaking ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-transparent'}`}>
                                                            {usersData[p.username]?.pfp ? (
                                                                <img src={usersData[p.username].pfp} alt={p.username} className="w-4 h-4 rounded-full object-cover" />
                                                            ) : (
                                                                <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-[8px] font-bold">
                                                                    {p.username[0]?.toUpperCase()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className={`text-xs truncate transition-colors ${p.isSpeaking ? 'text-white font-semibold' : 'text-white/60'}`}>{usersData[p.username]?.displayName || p.username}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Modal Footer / Extra Actions */}
                        <div className="flex-shrink-0 p-4 border-t border-white/10 bg-black/25 flex flex-col gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsNavigationModalOpen(false);
                                    setShowMembers(true);
                                }}
                                disabled={!canViewMembers}
                                className={`w-full py-3 px-4 rounded-xl font-bold flex items-center justify-between text-sm transition-all ${canViewMembers
                                        ? 'bg-white/10 text-white hover:bg-white/15 active:scale-98'
                                        : 'opacity-50 cursor-not-allowed text-white/50'
                                    }`}
                                title={!canViewMembers ? (!isMember ? 'Join to see members' : 'Only admins can view members') : undefined}
                            >
                                <span className="flex items-center gap-2">
                                    <UsersIcon className="w-4 h-4" />
                                    <span>Members List</span>
                                </span>
                                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-semibold">{community.members.length}</span>
                            </button>

                            {community.isPrivate && (
                                <div className="text-center py-1 bg-yellow-500/10 rounded-lg border border-yellow-500/20 text-[11px] font-bold text-yellow-500 uppercase tracking-widest">
                                    🔒 Private Community
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <MediaViewerModal
                isOpen={activeMediaViewerIndex !== null}
                onClose={() => setActiveMediaViewerIndex(null)}
                mediaList={channelMedia}
                initialIndex={activeMediaViewerIndex ?? 0}
            />
            <NativeCamera
                isOpen={showCamera}
                onClose={() => setShowCamera(false)}
                onCapture={handleCameraCapture}
                onOpenGallery={handleGalleryClick}
                captureMode={cameraMode}
            />
        </div>
    );
};
